const subscribers = {};
const trapFocusHandlers = {};

function isBot() {
  const ua = navigator.userAgent.toLowerCase();
  const botKeywords = ['headless', 'phantom', 'selenium', 'bot', 'crawler', 'lighthouse', 'gtmetrix', 'pagespeed'];
  if (botKeywords.some(k => ua.includes(k))) return true;
  if (navigator.webdriver) return true;
  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua);
  if (!isMobile) {
    if (!navigator.plugins || navigator.plugins.length === 0) return true;
  }
  if (!navigator.languages || navigator.languages.length === 0) return true;
  return false;
}

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) subscribers[eventName] = [];
  subscribers[eventName].push(callback);
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) subscribers[eventName].forEach((callback) => callback(data));
}

// One shared IntersectionObserver per threshold instead of one per element
const sharedObservers = new Map();

// Elements entering the viewport together are initialized one per task, so their init work
// doesn't add up to one long task that blocks the main thread
const pendingObserverCallbacks = [];
let flushingObserverCallbacks = false;

function yieldToMain() {
  if (window.scheduler && typeof window.scheduler.yield === 'function') return window.scheduler.yield();
  return new Promise((resolve) => setTimeout(resolve, 0));
}

async function flushObserverCallbacks() {
  if (flushingObserverCallbacks) return;
  flushingObserverCallbacks = true;
  while (pendingObserverCallbacks.length) {
    pendingObserverCallbacks.shift().forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error(error);
      }
    });
    if (pendingObserverCallbacks.length) await yieldToMain();
  }
  flushingObserverCallbacks = false;
}

function createObserver(options) {
  if (typeof options.callback !== 'function') return;
  const threshold = options.threshold ? options.threshold : 0;
  let shared = sharedObservers.get(threshold);
  if (!shared) {
    const callbacks = new WeakMap();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting || !callbacks.has(entry.target)) return;
        const targetCallbacks = callbacks.get(entry.target);
        callbacks.delete(entry.target);
        observer.unobserve(entry.target);
        pendingObserverCallbacks.push(targetCallbacks);
      });
      flushObserverCallbacks();
    }, { rootMargin: `0px 0px ${threshold}px 0px` });
    shared = { observer, callbacks };
    sharedObservers.set(threshold, shared);
  }
  const targetCallbacks = shared.callbacks.get(options.element);
  if (targetCallbacks) {
    targetCallbacks.push(options.callback);
  } else {
    shared.callbacks.set(options.element, [options.callback]);
    shared.observer.observe(options.element);
  }
}

function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  const expires = 'expires=' + d.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
}

function getCookie(cname) {
  const name = cname + '=';
  const ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }

  return '';
}

function deleteCookie(name) {
  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

function readCookie (name) {
  let list = [],
  data = getCookie(name);
  if (data !== null && data !== undefined && data !== '') list = data.split(' ');
  return list
}

function convertBreakpoint(breakpoint) {
  if (!breakpoint) return '';
  const bp = String(breakpoint).trim();
  if (bp.startsWith('(')) return bp;
  if (/^\\d+(\\.\\d+)?$/.test(bp)) return `(min-width: ${bp}px)`;

  let screen;
  switch (bp) {
    case 'small':
      screen = '(max-width: 749px)';
      break;
    case 'medium':
      screen = '(min-width: 750px) and (max-width: 1032px)';
      break;
    case 'large-up':
      screen = '(min-width: 1033px)';
      break;
    default:
      screen = bp; // fallback for custom tokens handled elsewhere
  }

  return screen;
}

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];
  removeTrapFocus();
  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = () => document.removeEventListener('keydown', trapFocusHandlers.keydown);

  trapFocusHandlers.keydown = function (event) {
    // If not TAB key
    if (event.code.toUpperCase() !== 'TAB') return;
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  elementToFocus.focus();

  if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) elementToFocus.setSelectionRange(0, elementToFocus.value.length);
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) mouseClick = false;
  });

  window.addEventListener('mousedown', (event) => mouseClick = true);

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');
  }, true);
}

function visibleMedia(media) {
  return Array.from(media).find((item) => {
    const style = window.getComputedStyle(item);
    return style.display !== 'none';
  });
}

const mediaPlaybackRequests = new WeakMap();
const mediaRegistry = {
  youtube: { selector: '.js-youtube', elements: new Set() },
  vimeo: { selector: '.js-vimeo', elements: new Set() },
  video: { selector: 'video', elements: new Set() },
  productModel: { selector: 'product-model', elements: new Set() }
};
const mediaRegistryObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) registerMediaElements(node);
    });
    mutation.removedNodes.forEach((node) => {
      if (node instanceof Element) unregisterMediaElements(node);
    });
  });
});
let mediaRegistryStarted = false;
let mediaResumeExclusions = null;

function normalizeScope(scope) {
  if (!scope) return [];
  const values = Array.isArray(scope) ? scope : [scope];
  return values
    .map((item) => typeof item === 'string' ? document.querySelector(item) : item)
    .filter((element) => element instanceof Element);
}

function collectMediaElements(root, type) {
  const entry = mediaRegistry[type];
  if (!entry || !root || typeof root.querySelectorAll !== 'function') return [];
  const elements = [];
  if (root instanceof Element && root.matches(entry.selector)) elements.push(root);
  elements.push(...root.querySelectorAll(entry.selector));
  return elements;
}

function registerMediaElements(root = document) {
  Object.keys(mediaRegistry).forEach((type) => {
    collectMediaElements(root, type).forEach((element) => mediaRegistry[type].elements.add(element));
  });
}

function unregisterMediaElements(root) {
  if (!root) return;
  Object.keys(mediaRegistry).forEach((type) => {
    collectMediaElements(root, type).forEach((element) => {
      mediaRegistry[type].elements.delete(element);
      mediaPlaybackRequests.delete(element);
    });
  });
}

function startMediaRegistry() {
  if (mediaRegistryStarted || !document.documentElement) return;
  mediaRegistryStarted = true;
  registerMediaElements(document);
  mediaRegistryObserver.observe(document.documentElement, { childList: true, subtree: true });
}

function getMediaElements(type, scope = null) {
  const entry = mediaRegistry[type];
  if (!entry) return [];
  const roots = normalizeScope(scope);

  if (roots.length) {
    const scopedElements = new Set();
    roots.forEach((root) => {
      collectMediaElements(root, type).forEach((element) => {
        scopedElements.add(element);
        entry.elements.add(element);
      });
    });
    return Array.from(scopedElements);
  }

  const connectedElements = [];
  entry.elements.forEach((element) => {
    if (element.isConnected) {
      connectedElements.push(element);
    } else {
      entry.elements.delete(element);
      mediaPlaybackRequests.delete(element);
    }
  });
  return connectedElements;
}

function shouldResumeMedia(element) {
  return Array.isArray(mediaResumeExclusions) && !mediaResumeExclusions.some((root) => root.contains(element));
}

function getMediaProvider(origin) {
  try {
    const hostname = new URL(origin).hostname;
    if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com')) return 'youtube';
    if (hostname === 'vimeo.com' || hostname.endsWith('.vimeo.com')) return 'vimeo';
  } catch {
    return null;
  }
  return null;
}

function getMessageFrame(source) {
  try {
    if (source?.frameElement) return source.frameElement;
  } catch {}

  return [...getMediaElements('youtube'), ...getMediaElements('vimeo')]
    .find((frame) => frame.contentWindow === source);
}

function handleMediaMessage(event) {
  const provider = getMediaProvider(event.origin);
  if (!provider) return;

  let data;
  try {
    data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
  } catch {
    return;
  }

  const video = getMessageFrame(event.source);
  const action = video && mediaPlaybackRequests.get(video);
  if (!video || !action) return;

  if (provider === 'youtube' && data?.event === 'infoDelivery') {
    const playerState = data.info?.playerState;
    if (action === 'pause' && playerState === 1) {
      if (shouldResumeMedia(video)) {
        video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        mediaPlaybackRequests.delete(video);
      } else {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        video.classList.add('paused');
      }
    } else if (action === 'pause' && playerState === 2) {
      if (shouldResumeMedia(video) && video.classList.contains('paused')) {
        video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        video.classList.remove('paused');
      }
      mediaPlaybackRequests.delete(video);
    } else if (action === 'play' && playerState === 2) {
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (action === 'play' && playerState === 1) {
      video.classList.remove('paused');
      mediaPlaybackRequests.delete(video);
    }
  }

  if (provider === 'vimeo' && data?.method === 'getPaused') {
    if (action === 'pause' && !data.value) {
      if (!shouldResumeMedia(video)) {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
        video.classList.add('paused');
      }
      mediaPlaybackRequests.delete(video);
    } else if (action === 'pause') {
      mediaPlaybackRequests.delete(video);
    } else if (action === 'play' && data.value) {
      video.contentWindow.postMessage('{"method":"play"}', '*');
      video.classList.remove('paused');
      mediaPlaybackRequests.delete(video);
    } else if (action === 'play') {
      video.classList.remove('paused');
      mediaPlaybackRequests.delete(video);
    }
  }
}

window.addEventListener('message', handleMediaMessage);

function pauseAllMedia(scope = null) {
  mediaResumeExclusions = null;

  getMediaElements('youtube', scope).forEach((video) => {
    mediaPlaybackRequests.set(video, 'pause');
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });

  getMediaElements('vimeo', scope).forEach((video) => {
    mediaPlaybackRequests.set(video, 'pause');
    video.contentWindow.postMessage('{"method":"getPaused"}', '*');
  });

  getMediaElements('video', scope).forEach((video) => {
    if (!video.paused && !video.ended) {
      video.pause();
      video.classList.add('paused');
    }
  });

  getMediaElements('productModel', scope).forEach((model) => {
    if (model.modelViewerUI && model.modelViewerUI.isPaused) {
      model.modelViewerUI.play();
      model.classList.add('paused');
    }
  });
}

function playAllPausedMedia(scope = null) {
  const excludeRoots = normalizeScope(scope);
  mediaResumeExclusions = excludeRoots;
  const isExcluded = (element) => excludeRoots.length && excludeRoots.some(root => root.contains(element));

  getMediaElements('youtube').forEach((video) => {
    if (!video.classList.contains('paused') || isExcluded(video)) return;
    mediaPlaybackRequests.set(video, 'play');
    video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    video.classList.remove('paused');
  });

  getMediaElements('vimeo').forEach((video) => {
    if (!video.classList.contains('paused') || isExcluded(video)) return;
    mediaPlaybackRequests.set(video, 'play');
    video.contentWindow.postMessage('{"method":"getPaused"}', '*');
  });

  getMediaElements('video').forEach((video) => {
    if (!video.classList.contains('paused') || isExcluded(video)) return;
    video.play();
    video.classList.remove('paused');
  });

  getMediaElements('productModel').forEach((model) => {
    if (!model.classList.contains('paused') || isExcluded(model)) return;
    if (model.modelViewerUI && model.modelViewerUI.isPaused) {
      model.modelViewerUI.play();
      model.classList.remove('paused');
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startMediaRegistry, { once: true });
} else {
  startMediaRegistry();
}

document.addEventListener('shopify:section:load', (event) => registerMediaElements(event.target));
document.addEventListener('shopify:section:unload', (event) => unregisterMediaElements(event.target));

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  }
}

function injectHTMLWithScripts(element, html) {
  element.innerHTML = html;
  // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  element.querySelectorAll('script').forEach((oldScriptTag) => {
    const newScriptTag = document.createElement('script');
    Array.from(oldScriptTag.attributes).forEach((attribute) => newScriptTag.setAttribute(attribute.name, attribute.value));
    newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
    oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
  });
  registerMediaElements(element);
}

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});

try {
  document.querySelector(':focus-visible');
  const options = Object.defineProperty({}, 'passive', {
    get() {
      ThemeSupportsPassive = true;
      return true;
    }
  });
} catch (error) {
  focusVisiblePolyfill();
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};
  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.formatMoney = function (cents, format) {
  if (typeof cents == 'string') cents = cents.replace('.', '');
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || window.shopMoney.money_format);

  function defaultOption(opt, def) {
    return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');
    if (isNaN(number) || number == null) return 0;
    number = number.toFixed(precision);

    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);
    var shouldHideProvinceContainer = this.provinceContainer && this.provinceContainer.dataset.hideOnNoProvinces !== 'false';

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      if (shouldHideProvinceContainer) {
        this.provinceContainer.style.display = 'none';
      } else {
        this.provinceContainer.style.display = '';
      }
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }
      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class AlertElement extends HTMLElement {
  constructor() {
    super();
    this._hideTimer = null;
    this._transitionTimer = null;
    this._showFrame = null;
    this._remaining = 0;
    this._startedAt = 0;
    this._onMouseEnter = this._pauseTimer.bind(this);
    this._onMouseLeave = this._resumeTimer.bind(this);
    this._finishHide = this._finishHide.bind(this);
  }

  static get observedAttributes() { return ['type']; }

  connectedCallback() {
    const template = this.querySelector(':scope > template');
    if (template instanceof HTMLTemplateElement) {
      template.replaceWith(template.content.cloneNode(true));
    }

    this._msgEl = this.querySelector('.form__message-text');
    this._boxEl = this.querySelector('.form__message');
    this.classList.remove('no-js-hidden');
    this.setAttribute('aria-hidden', 'true');
    this._defaultDuration = parseInt(this.getAttribute('data-duration'), 10) || 5000;
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);
    if (this.hasAttribute('type')) this._applyType(this.getAttribute('type'));
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    this._clearTimer();
    this._clearTransition();
    cancelAnimationFrame(this._showFrame);
    this._showFrame = null;
  }

  attributeChangedCallback(name, oldV, newV) {
    if (name === 'type' && oldV !== newV && this.isConnected) {
      this._applyType(newV, oldV);
    }
  }

  show(message, { type = null, duration = null, announce = true } = {}) {
    if (typeof message === 'string') this._setMessage(message);
    if (type) this.type = type;
    this._clearTransition();
    cancelAnimationFrame(this._showFrame);
    this.hidden = false;
    this._showFrame = requestAnimationFrame(() => {
      this._showFrame = null;
      if (this.isConnected) this.setAttribute('open', '');
    });
    if (announce) this._announce(this._msgEl?.textContent || '');
    const ms = Number.isFinite(duration) ? duration : this._defaultDuration;
    this._startTimer(ms);
  }


  hide() {
    if (this._boxEl?.contains(document.activeElement) && this._returnFocus?.isConnected) {
      this._returnFocus.focus({ preventScroll: true });
    }
    this._clearTimer();
    this._clearTransition();
    this.removeAttribute('open');
    this._boxEl.addEventListener('transitionend', this._finishHide, { once: true });
    this._transitionTimer = setTimeout(this._finishHide, 250);
  }

  _finishHide() {
    this.hidden = true;
    clearTimeout(this._announceTimer);
    if (this._region) this._region.textContent = '';
    this._region = null;
    if (this._messageEl !== document.activeElement) this._removeFocusMessage();
    this._clearTransition();
  }

  _clearTransition() {
    if (this._transitionTimer) clearTimeout(this._transitionTimer);
    this._transitionTimer = null;
    this._boxEl?.removeEventListener('transitionend', this._finishHide);
  }

  // The alert itself is hidden from assistive tech: it starts as display:none, so screen readers
  // miss it, and it is ignored while an aria-modal dialog is open. Errors next to a focused control
  // get a focusable message; anything else goes to a live region that is already in the page, or
  // inside the open dialog.
  _announce(text) {
    const target = document.activeElement;
    if (this.type === 'error' && target && target !== document.body) {
      const inModal = target.closest('[aria-modal="true"]');
      clearTimeout(this._announceTimer);
      this._announceTimer = setTimeout(() => {
        inModal ? this._focusMessage(target, text) : this._focusAlert(target);
      }, 150);
      return;
    }

    const container = document.activeElement?.closest('[aria-modal="true"]') || document.body;
    let region = container.querySelector(':scope > [data-alert-region]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'visually-hidden';
      region.setAttribute('role', 'alert');
      region.setAttribute('aria-live', 'assertive');
      region.setAttribute('aria-atomic', 'true');
      region.dataset.alertRegion = '';
      container.prepend(region);
    }
    region.textContent = '';
    clearTimeout(this._announceTimer);
    this._announceTimer = setTimeout(() => {
      region.textContent = text;
    }, 150);
    this._region = region;
  }

  // Some screen readers only read what receives focus, so an error is put in a focusable message
  // right after the focused control. Tab order continues from there and it is removed on blur.
  _focusMessage(target, text) {
    if (!target.isConnected) return;

    this._removeFocusMessage();
    const message = document.createElement('div');
    message.className = 'visually-hidden';
    message.tabIndex = -1;
    message.textContent = text;
    target.after(message);
    this._messageEl = message;
    message.addEventListener('blur', () => {
      message.remove();
      if (this._messageEl === message) this._messageEl = null;
    }, { once: true });
    message.focus({ preventScroll: true });
  }

  // Focus the visible alert itself; it stays open while focused, and Tab, Shift+Tab or Escape
  // return focus to the control that raised it.
  _focusAlert(target) {
    if (!this._boxEl || this.hidden) return;

    this._returnFocus = target;
    this.removeAttribute('aria-hidden');
    this._boxEl.tabIndex = -1;
    this._boxEl.addEventListener('keydown', this._onAlertKeyDown ||= (event) => {
      if (event.key !== 'Tab' && event.key !== 'Escape') return;
      event.preventDefault();
      this._returnFocus?.focus({ preventScroll: true });
    });
    this._boxEl.addEventListener('focusout', this._onAlertFocusOut ||= () => {
      this.setAttribute('aria-hidden', 'true');
      this._boxEl.removeAttribute('tabindex');
      this._resumeTimer();
    }, { once: true });
    this._pauseTimer();
    this._boxEl.focus({ preventScroll: true });
  }

  _removeFocusMessage() {
    const message = this._messageEl;
    this._messageEl = null;
    message?.remove();
  }

  get type() { return this.getAttribute('type') || 'info'; }
  set type(val) { if (val) this.setAttribute('type', val); }

  _setMessage(text) {
    if (this._msgEl) this._msgEl.textContent = text;
  }

  _applyType(next, prev) {
    if (!this._boxEl) return;
    if (prev) this._boxEl.classList.remove(`form__message--${prev}`);
    if (next) this._boxEl.classList.add(`form__message--${next}`);
  }

  _startTimer(ms) {
    this._clearTimer();
    if (!ms || ms <= 0) return;
    this._remaining = ms;
    this._startedAt = performance.now();
    this._hideTimer = setTimeout(() => this.hide(), this._remaining);
  }

  _pauseTimer() {
    if (!this._hideTimer) return;
    clearTimeout(this._hideTimer);
    this._hideTimer = null;
    const elapsed = performance.now() - this._startedAt;
    this._remaining = Math.max(0, this._remaining - elapsed);
  }

  _resumeTimer() {
    if (this._hideTimer || this._remaining <= 0) return;
    this._startedAt = performance.now();
    this._hideTimer = setTimeout(() => this.hide(), this._remaining);
  }

  _clearTimer() {
    if (this._hideTimer) clearTimeout(this._hideTimer);
    this._hideTimer = null;
  }
}

customElements.define('alert-element', AlertElement);

class AnchorLink extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.link = this.querySelector('a[href^="#"]') || (this.matches('a[href^="#"]') ? this : null);
    this.targetSelector = this.dataset.target || this.getAttribute('href') || this.link?.getAttribute('href');
    if (!this.targetSelector || !this.targetSelector.startsWith('#')) return;
    this.link?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.onClick);
  }

  get modal() {
    return this._modal = this._modal || this.closest('modal-dialog')
  }

  get drawer() {
    return this._drawer = this._drawer || this.closest('side-drawer')
  }

  get target() {
    return document.querySelector(this.targetSelector);
  }

  onClick(event) {
    const target = this.target;
    if (!target) return;
    event.preventDefault();
    const headerOffset = Number(this.dataset.offset || 0);
    const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    if(this.modal) this.modal.hide();
    if(this.drawer) this.drawer.close();
    window.scrollTo({
      top,
      behavior: this.dataset.behavior || 'smooth'
    });

    if (this.dataset.updateHash !== 'false') {
      history.pushState(null, '', this.targetSelector);
    }
  }
}

customElements.define('anchor-link', AnchorLink);

class HoverElement extends HTMLElement {
  // Updates are batched per frame: measure every element first, then write,
  // so the page is laid out once instead of once per element
  static queue = new Set();
  static frame = null;
  static flush() {
    HoverElement.frame = null;
    const elements = [...HoverElement.queue];
    HoverElement.queue.clear();
    const measurements = elements.map((element) => element.measure());
    elements.forEach((element, index) => element.apply(measurements[index]));
  }

  constructor() {
    super();
    this.resizeObserver = new ResizeObserver(() => this._scheduleUpdate());
    this.mutationObserver = new MutationObserver(() => this._scheduleUpdate());
    this.mql = null;
    this._onMqlChange = null;
    this._connected = false;
    this._lastPx = null;
  }

  static get observedAttributes() {
    return ['data-breakpoint', 'data-parent', 'data-type'];
  }

  get breakpoint() {
    return this.dataset.breakpoint || null
  }

  get parentEl() {
    const sel = this.dataset.parent;
    if (!sel) return this.parentElement ?? null;
    try { 
      return this.closest(sel)
    } catch {
      return this.parentElement ?? null
    }
  }

  get type() {
    return this.dataset.type || 'spacing'
  }

  connectedCallback() {
    this._connected = true;
    if (this.type === 'none') return;
    this.init();
    this.resizeObserver.observe(this);
    this.mutationObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this._connected = false;
    this.resizeObserver.disconnect();
    this.mutationObserver.disconnect();
    if (this.mql && this._onMqlChange) {
      this.mql.removeEventListener('change', this._onMqlChange);
    }
    if (this.parentEl) this.parentEl.style.removeProperty(`--hover-element-${this.type}`);
    HoverElement.queue.delete(this);
    this._lastPx = null;
  }

  init() {
    if (this.type === 'none') return;
    this._setupMediaQuery();
    this._scheduleUpdate();
  }

  _scheduleUpdate() {
    if (this.type === 'none') return;
    HoverElement.queue.add(this);
    if (HoverElement.frame === null) HoverElement.frame = requestAnimationFrame(HoverElement.flush);
  }

  // Immediate update, used by other components (hotspot.js, look-grid.js)
  update() {
    this.apply(this.measure());
  }

  // Read phase: no style writes here
  measure() {
    if (this.type === 'none') return null;
    const parent = this.parentEl;
    if (!parent) return null;

    const varName = `--hover-element-${this.type}`;
    const matches = this.mql ? this.mql.matches : true;
    if (!matches) return { parent, varName, px: null };

    const isHidden = this.offsetParent === null && getComputedStyle(this).display === 'none';
    if (isHidden) return null;
    return { parent, varName, px: `${this.offsetHeight}px` };
  }

  // Write phase
  apply(measurement) {
    if (!measurement) return;
    const { parent, varName, px } = measurement;
    if (px === null) {
      parent.style.removeProperty(varName);
      this._lastPx = null;
      return;
    }
    if (px !== this._lastPx) {
      parent.style.setProperty(varName, px);
      this._lastPx = px;
    }
  }

  _setupMediaQuery() {
    if (this.mql && this._onMqlChange) {
      this.mql.removeEventListener('change', this._onMqlChange);
    }

    if (!this.breakpoint) {
      this.mql = null;
      this._onMqlChange = null;
      return;
    }

    const query = typeof convertBreakpoint === 'function' ? convertBreakpoint(this.breakpoint) : this.breakpoint;
    this.mql = window.matchMedia(query);
    this._onMqlChange = () => this._scheduleUpdate();
    this.mql.addEventListener('change', this._onMqlChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Initial attributes are handled by connectedCallback
    if (!this._connected || oldValue === newValue) return;
    if (name === 'data-type') {
      if (newValue === 'none') {
        this.resizeObserver.disconnect();
        this.mutationObserver.disconnect();
        if (this.mql && this._onMqlChange) {
          this.mql.removeEventListener('change', this._onMqlChange);
          this.mql = null;
          this._onMqlChange = null;
        }
        if (this.parentEl && oldValue) {
          this.parentEl.style.removeProperty(`--hover-element-${oldValue}`);
        }
        HoverElement.queue.delete(this);
        this._lastPx = null;
        return;
      } else if (oldValue === 'none') {
        this.init();
        this.resizeObserver.observe(this);
        this.mutationObserver.observe(this, { childList: true, subtree: true, characterData: true });
      }
    }
    if (name === 'data-breakpoint') this._setupMediaQuery();
    this._scheduleUpdate();
  }
}

customElements.define('hover-element', HoverElement);

class LinkHoverElement extends HTMLElement {
  constructor() {
    super();
    this._isActive = false;
    this._hoverOpenTimer = null;
    this._hoverCloseTimer = null;
    this._lastSlideIndex = null;

    this.mutationObserver = new MutationObserver(() => {
      if (this._isActive && this.type !== 'slideshow') this.update(true);
    });

    this.mql = null;
    this._onMqlChange = () => { if (this._isActive) this.update(true); };

    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
  }

  static get observedAttributes() {
    return [
      'data-breakpoint',
      'data-parent',
      'data-origin-image-src',
      'data-origin-image-srcset',
      'data-change-image-src',
      'data-change-image-srcset',
      'data-type',
      'data-target-id',
      'data-inspector-focus'
    ];
  }

  get breakpoint() {
    return this.dataset.breakpoint || null
  }

  get type() {
    return this.dataset.type
  }

  get parentEl() {
    const sel = this.dataset.parent;
    if (!sel) return this.parentElement ?? null;
    try {
      return this.closest(sel)
    } catch {
      return this.parentElement ?? null
    }
  }

  get targetSelector() {
    return this.dataset.targetId || ''
  }

  get originImageSrc() {
    return this.dataset.originImageSrc || ''
  }

  get originImageSrcset() {
    return this.dataset.originImageSrcset || ''
  }

  get changeImageSrc() {
    return this.dataset.changeImageSrc || ''
  }

  get changeImageSrcset() {
    return this.dataset.changeImageSrcset || ''
  }

  connectedCallback() {
    this._setupMediaQuery();
    this._preloadHoverImage();
    const target = this.parentEl || this;
    this.mutationObserver.observe(target, { childList: true, subtree: true, characterData: true });
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
  }

  disconnectedCallback() {
    this.mutationObserver.disconnect();
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    if (this.mql && this._onMqlChange) this.mql.removeEventListener('change', this._onMqlChange);
    clearTimeout(this._hoverOpenTimer);
    clearTimeout(this._hoverCloseTimer);
  }

  onPointerEnter() {
    clearTimeout(this._hoverCloseTimer);
    clearTimeout(this._hoverOpenTimer);
    this._hoverOpenTimer = setTimeout(() => {
      this._isActive = true;
      this.update(true);
    }, 60);
  }

  onPointerLeave() {
    clearTimeout(this._hoverOpenTimer);
    clearTimeout(this._hoverCloseTimer);
    this._hoverCloseTimer = setTimeout(() => {
      this._isActive = false;
      this.update(false);
    }, 150);
  }

  update(change) {
    if (typeof change !== 'boolean') return;
    const matches = this.mql ? this.mql.matches : true;
    const useHover = matches && change;

    if (!useHover && this.type === 'slideshow') {
      this._lastSlideIndex = null;
    }

    if (this.type === 'inspector') {
      this._applyInspector(useHover);
    } else if (this.type === 'slideshow') {
      this._applySlideshow(useHover);
    } else {
      this._applyImageSwap(useHover);
    }
  }

  _applyImageSwap(useHover) {
    const parent = this.parentEl;
    if (!parent) return;

    const img = parent.querySelector('img');
    if (!img) return;

    const nextSrc = useHover ? (this.changeImageSrc || '') : (this.originImageSrc || '');
    const nextSrcset = useHover ? (this.changeImageSrcset || '') : (this.originImageSrcset || '');
    const abs = u => (u ? new URL(u, document.baseURI).href : '');
    const current = img.currentSrc || img.src || '';
    const wantAbs = abs(nextSrc);

    if (nextSrcset) {
      if (img.getAttribute('srcset') !== nextSrcset) img.setAttribute('srcset', nextSrcset);
    } else if (img.hasAttribute('srcset')) {
      img.removeAttribute('srcset');
    }

    if (nextSrc) {
      if ((img.getAttribute('src') || '') !== nextSrc && current !== wantAbs) {
        img.setAttribute('src', nextSrc);
      }
    }
  }

  _applyInspector(useHover) {
    const scope = this.parentEl || document;
    if (!this.targetSelector) return;
    let target = null;
    try {
      target = scope.querySelector(this.targetSelector);
    } catch {
      target = null;
    }
    if (!target) return;
    target.classList.toggle('active', !!useHover);
  }

  _applySlideshow(useHover) {
    if (!useHover) return;

    const scope = this.parentEl || document;
    if (!this.targetSelector) return;

    let target;

    try {
      target = scope.querySelector(this.targetSelector);
    } catch {
      return;
    }

    if (!target) return;

    const swiperEl = this.parentEl?.querySelector('[data-swiper]');
    if (!swiperEl) return;

    const slideEl = target.classList?.contains('swiper-slide')
      ? target
      : target.closest('.swiper-slide');

    const indexStr = slideEl?.dataset?.index;
    const index = Number.parseInt(indexStr, 10);
    if (Number.isNaN(index)) return;

    if (index === this._lastSlideIndex) return;
    this._lastSlideIndex = index;

    if (typeof swiperEl.setActiveSlideItem === 'function') {
      swiperEl.setActiveSlideItem(index);
    }
  }

  _setupMediaQuery() {
    if (this.mql && this._onMqlChange) this.mql.removeEventListener('change', this._onMqlChange);
    if (!this.breakpoint) {
      this.mql = null;
      return
    }

    const query = typeof convertBreakpoint === 'function' ? convertBreakpoint(this.breakpoint) : this.breakpoint;
    this.mql = window.matchMedia(query);
    this.mql.addEventListener('change', this._onMqlChange);
  }

  _preloadHoverImage() {
    if (this.changeImageSrc) {
      const i = new Image();
      i.decoding = 'async';
      i.src = this.changeImageSrc;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-breakpoint') this._setupMediaQuery();
    if (name === 'data-change-image-src') this._preloadHoverImage();
    if (name === 'data-type' || name === 'data-target-id') {
      if (this._isActive) this.update(true);
    }
  }
}

customElements.define('link-hover-element', LinkHoverElement);


class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this._open = this.details.hasAttribute('open');
    this._currentWidth = window.innerWidth;
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  get open() {
    return this._open
  }

  set open(value) {
    value = Boolean(value);
    if (value !== this._open) {
      this._open = value;
      if (value) {
        this.details.setAttribute('open', '');
      } else {
        this.details.removeAttribute('open');
      }
    }

    this.details.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this } }));
  }

  get details() {
    return this._details || this.querySelector('details')
  }

  get summaryElement() {
    return this._summaryElement || this.querySelector('summary')
  }

  get contentElement() {
    return this._contentElement || this.querySelector('summary + *')
  }

  get state(){
    return this.details.hasAttribute('open')
  }

  get breakpoint() {
    return this.dataset.breakpoint
  }

  connectedCallback() {
    this.onInitilize();
    this.summaryElement?.addEventListener('click', this.onSummaryClick);
    this.details.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener('click', this.onSummaryClick);
    this.details.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('resize', this.onResize);
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.toggle();
  }

  toggle(forceState = !this.open) {
    this.open = forceState;
  }

  openDetails() {
    this.open = true;
  }

  closeDetails() {
    this.open = false;
  }

  onInitilize() {
    this.update();
  }

  update() {
    if (this.breakpoint == 'all' || this.breakpoint === undefined) return;

    const breakpoints = String(this.breakpoint).split(',').map((breakpoint) => breakpoint.trim()).filter(Boolean);
    const matchesBreakpoint = breakpoints.some((breakpoint) => {
      const query = convertBreakpoint(breakpoint);
      return query ? window.matchMedia(query).matches : false;
    });

    if (!matchesBreakpoint) {
      this.openDetails();
    } else if (this.dataset.open === undefined) {
      this.closeDetails();
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.closeDetails();
    }
  }
  
  onResize() {
    const newWidth = window.innerWidth;
    if (newWidth !== this._currentWidth) {
      this._currentWidth = newWidth;
      this.update();
    }
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class DetailsDisclosures extends HTMLElement {
  constructor() {
    super();
    this._onToggle = this._onToggle.bind(this);
    this.onResize = this.onResize.bind(this);
    this._currentWidth = window.innerWidth;
  }

  connectedCallback() {
    if (!this.onlyOne) return;
    this.observeDetails();
    this.addEventListeners();
    window.addEventListener('resize', this.onResize);
    if (this.onlyOneActive) this.enforceSingleOpen();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    window.removeEventListener('resize', this.onResize);
    if (this.observer) this.observer.disconnect();
  }

  get onlyOne() {
    return this.hasAttribute('only-one');
  }

  get onlyOneBreakpoint() {
    return this.dataset.onlyOneBreakpoint;
  }

  get onlyOneActive() {
    if (!this.onlyOne) return false;
    if (!this.onlyOneBreakpoint) return true;

    const breakpoints = String(this.onlyOneBreakpoint)
      .split(',')
      .map((breakpoint) => breakpoint.trim())
      .filter(Boolean);

    return breakpoints.some((breakpoint) => {
      const query = typeof convertBreakpoint === 'function' ? convertBreakpoint(breakpoint) : breakpoint;
      return query ? window.matchMedia(query).matches : false;
    });
  }

  get allDetailsDisclosure() {
    return this._allDetailsDisclosure = Array.from(this.querySelectorAll('details-disclosure'));
  }

  addEventListeners() {
    this.allDetailsDisclosure.forEach(detailsDisclosure => detailsDisclosure.addEventListener('toggle', this._onToggle));
  }

  removeEventListeners() {
    if (!this._allDetailsDisclosure) return;
    this._allDetailsDisclosure.forEach(detailsDisclosure => detailsDisclosure.removeEventListener('toggle', this._onToggle));
  }

  onResize() {
    const newWidth = window.innerWidth;
    if (newWidth === this._currentWidth) return;
    this._currentWidth = newWidth;
    if (this.onlyOneActive) this.enforceSingleOpen();
  }

  _onToggle(event) {
    const current = event.currentTarget;
    if (!current.open || !this.onlyOneActive) return;
    this.allDetailsDisclosure.forEach(detailsDisclosure => {
      if (detailsDisclosure !== current) {
        requestAnimationFrame(() => detailsDisclosure.toggle(false));
      }
    });
  }

  enforceSingleOpen() {
    if (!this.onlyOneActive) return;
    let firstOpenFound = false;
    this.allDetailsDisclosure.forEach(detailsDisclosure => {
      if (detailsDisclosure.open) {
        if (!firstOpenFound) {
          firstOpenFound = true;
        } else {
          detailsDisclosure.toggle(false);
        }
      }
    });
  }

  observeDetails() {
    this.observer = new MutationObserver(() => {
      this.removeEventListeners();
      this._allDetailsDisclosure = null;
      if (this.onlyOneActive) this.enforceSingleOpen();
      this.addEventListeners();
    });

    this.observer.observe(this, { childList: true, subtree: true });
  }
}

customElements.define('details-disclosures', DetailsDisclosures);

class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onBodyClickEvent = this.onBodyClick.bind(this);
    this.summaryToggle.setAttribute('role', 'button');
  }

  connectedCallback() {
    this.detailsContainer.addEventListener('keyup', this.onKeyUp);
    this.summaryToggle.addEventListener('click', this.onSummaryClick);
    this.overlay?.addEventListener('click', this.onSummaryClick);
  }

  disconnectedCallback() {
    this.detailsContainer.removeEventListener('keyup', this.onKeyUp);
    this.summaryToggle.removeEventListener('click', this.onSummaryClick);
    this.overlay?.removeEventListener('click', this.onSummaryClick);
    if (this.isOpen()) {
      this.close(false);
    } else {
      document.body.removeEventListener('click', this.onBodyClickEvent);
    }
  }

  get overlay() {
    return this.querySelector('[id^="Modal-Overlay-"]')
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open')
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.close();
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');
    trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]'), this.detailsContainer.querySelector('input:not([type="hidden"])'));
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);

class DropdownDisclosure extends HTMLElement {
  constructor() {
    super();
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
  }

  connectedCallback() {
    this.summary?.setAttribute('role', 'button');
    this.summary?.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
    this.summary?.addEventListener('click', this.onSummaryClick);
    this.details?.addEventListener('keyup', this.onKeyUp);
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onSummaryClick);
    this.details?.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onBodyClick);
  }

  get details() {
    return this._details || (this._details = this.querySelector('details'));
  }

  get summary() {
    return this._summary || (this._summary = this.querySelector('summary'));
  }

  get group() {
    return this.dataset.group;
  }

  isOpen() {
    return !!this.details?.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') this.close();
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  open() {
    if (!this.details || this.isOpen()) return;
    if (this.group) DropdownDisclosure.closeGroup(this.group, this);
    this.details.setAttribute('open', '');
    this.summary?.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.details || !this.isOpen()) return;
    this.details.removeAttribute('open');
    this.summary?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onBodyClick);
  }

  static closeGroup(group, except) {
    document.querySelectorAll('dropdown-disclosure').forEach((el) => {
      if (el !== except && el.group === group) el.close();
    });
  }
}

customElements.define('dropdown-disclosure', DropdownDisclosure);

class FormElement extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleFieldInteraction = this.handleFieldInteraction.bind(this);
  }

  connectedCallback() {
    const templateForm = this.querySelector('.template-form');
    this.form = this.querySelector('form');

    if (templateForm && !this.getAttribute('loaded')) {
      this.message = templateForm.content.firstElementChild.cloneNode(true);
      this.messageContent = this.message.innerHTML || '';
      this.messageType = this.message.dataset.messageType;
      this.handleMessage();
      this.setAttribute('loaded', true);
    }

    if (!this.form || this.validationLoaded) return;
    this.form.setAttribute('novalidate', 'novalidate');
    this.form.noValidate = true;
    this.form.addEventListener('submit', this.handleSubmit, true);
    this.form.addEventListener('input', this.handleFieldInteraction);
    this.form.addEventListener('change', this.handleFieldInteraction);
    this.form.addEventListener('blur', this.handleFieldInteraction, true);
    this.validationLoaded = true;
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('submit', this.handleSubmit, true);
    this.form.removeEventListener('input', this.handleFieldInteraction);
    this.form.removeEventListener('change', this.handleFieldInteraction);
    this.form.removeEventListener('blur', this.handleFieldInteraction, true);
    this.validationLoaded = false;
  }

  handleSubmit(event) {
    if (this.isSubmitting) {
      this.isSubmitting = false;
      return;
    }

    event.preventDefault();

    if (!this.validateRequiredFields()) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      const firstInvalidField = this.form.querySelector('[aria-invalid="true"]');
      if (firstInvalidField) firstInvalidField.focus();
      return false;
    }

    this.isSubmitting = true;
    this.form.setAttribute('novalidate', 'novalidate');
    this.form.noValidate = true;
    this.form.requestSubmit(event.submitter || undefined);
    return true;
  }

  handleFieldInteraction(event) {
    const field = event.target.closest('input, select, textarea');
    if (!field || !this.form?.contains(field) || !this.isRequiredField(field)) return;
    this.validateField(field);
  }

  validateRequiredFields() {
    const requiredFields = this.getRequiredFields();
    let isFormValid = true;

    requiredFields.forEach((field) => {
      if (!this.validateField(field)) isFormValid = false;
    });

    return isFormValid
  }

  getRequiredFields() {
    if (!this.form) return [];

    const requiredFields = Array.from(this.form.querySelectorAll('input[required], select[required], textarea[required]')).filter((field) => !field.disabled);
    const groupedFieldNames = new Set();

    return requiredFields.filter((field) => {
      if (!['checkbox', 'radio'].includes(field.type)) return true;
      if (groupedFieldNames.has(field.name)) return false;
      groupedFieldNames.add(field.name);
      return true;
    });
  }

  validateField(field) {
    if (!this.isRequiredField(field)) return true;
    const isGroupedField = ['checkbox', 'radio'].includes(field.type);
    const groupFields = isGroupedField ? this.getGroupedFields(field) : [field];
    const validationTarget = groupFields[0];
    const isValid = isGroupedField ? this.isGroupedFieldValid(field) : field.checkValidity();
    const message = isValid ? '' : (validationTarget.validationMessage || window.formStrings.required);

    groupFields.forEach((groupField) => {
      this.toggleFieldError(groupField, message);
    });

    return isValid
  }

  isRequiredField(field) {
    return field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement
      ? field.hasAttribute('required')
      : false;
  }

  getGroupedFields(field) {
    if (!field.name || !this.form) return [field];
    return Array.from(this.form.querySelectorAll(`[name="${CSS.escape(field.name)}"]`))
  }

  isGroupedFieldValid(field) {
    return this.getGroupedFields(field).some((groupField) => groupField.checked)
  }

  toggleFieldError(field, message = '') {
    const fieldWrapper = field.closest('.form__field');
    if (!fieldWrapper) return;
    const messageElement = this.getFieldMessageElement(field, fieldWrapper);
    const hasError = Boolean(message);
    fieldWrapper.classList.toggle('form__field--error', hasError);
    field.setAttribute('aria-invalid', String(hasError));
    if (!hasError) {
      messageElement.classList.add('hidden');
      messageElement.querySelector('.error-message').textContent = '';
      field.removeAttribute('aria-describedby');
      return;
    }

    field.setAttribute('aria-describedby', messageElement.id);
    messageElement.classList.remove('hidden');
    messageElement.querySelector('.error-message').textContent = message;
  }

  getFieldMessageElement(field, fieldWrapper) {
    const fieldId = field.id || `${field.name.replace(/[^a-zA-Z0-9_-]/g, '-')}-field`;
    const messageId = `${fieldId}-error`;
    let messageElement = fieldWrapper.querySelector(`#${CSS.escape(messageId)}`);
    if (messageElement) return messageElement;
    messageElement = document.createElement('div');
    messageElement.id = messageId;
    messageElement.className = 'form__message form__message--inline hidden';
    messageElement.innerHTML = '<small class="error-message text-xsmall break"></small>';
    fieldWrapper.appendChild(messageElement);

    return messageElement
  }

  handleMessage() {
    switch (this.messageType) {
      case 'error':
        this.handleErrorMessage(this.messageContent);
        break;
      case 'success':
        this.handleSuccessMessage(this.messageContent);
        break;
      default:
        break;
    }
  }

  handleSuccessMessage(successMessage = false) {
    if (this.hideAlert || successMessage == false) return;
    this.successAlert = this.successAlert || this.querySelector(`[id^="Alert-Success-"]`) || document.querySelector(`[id="Alert-Success"]`);
    if (!this.successAlert) return;
    this.successAlert.show(successMessage, { type: 'success', duration: ON_VISIBLE_DEBOUNCE_TIMER });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideAlert || errorMessage == false) return;
    this.errorAlert = this.errorAlert || this.querySelector(`[id^="Alert-Error-"]`) || document.querySelector(`[id="Alert-Error"]`);
    if (!this.errorAlert) return;
    this.errorAlert.show(errorMessage, { type: 'error', duration: ON_VISIBLE_DEBOUNCE_TIMER });
  }
}

customElements.define('form-element', FormElement);

class HeaderMenu extends HTMLElement {
  constructor() {
    super();

    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onInteractionKeyDown = this.onInteractionKeyDown.bind(this);
    this.computePadding = this.computePadding.bind(this);
    this.updateSubmenuPosition = this.updateSubmenuPosition.bind(this);
    this.resetSubmenuPosition = this.resetSubmenuPosition.bind(this);
    this.updateParentLevel3Position = this.updateParentLevel3Position.bind(this);
    this.onResize = this.onResize.bind(this);
    this._isOpen = false;
    this._hoverOpenTimer = null;
    this._hoverCloseTimer = null;
    this._lastInteraction = 'pointer';
    this._openOnTouchClick = false;
  }

  static instances = new Set();
  static groups = new Map();
  static openSequence = 0;
  static observedHeaders = new Map();
  static resizeObserver = new ResizeObserver((entries) => {
    const headerChanged = entries.some((entry) => HeaderMenu.observedHeaders.has(entry.target));

    entries.forEach((entry) => {
      if (entry.target instanceof HeaderMenu) entry.target.onResize();
    });

    if (headerChanged) HeaderMenu.instances.forEach((instance) => instance.onResize());
  });
  static onWindowResize = () => HeaderMenu.instances.forEach((instance) => instance.onResize());
  // Layout updates of all menus share one frame: measure everything first, then write
  static layoutQueue = new Set();
  static layoutFrame = null;
  static flushLayout = () => {
    HeaderMenu.layoutFrame = null;
    const instances = [...HeaderMenu.layoutQueue].filter((instance) => instance.isConnected);
    HeaderMenu.layoutQueue.clear();
    const paddings = instances.map((instance) => instance.measurePadding());
    instances.forEach((instance, index) => instance.applyPadding(paddings[index]));
    instances.forEach((instance) => {
      if (instance._isOpen) instance.updateSubmenuPositionNow();
    });
  };

  static addToGroup(instance) {
    const root = instance._groupRoot();
    const group = this.groups.get(root) || new Set();
    group.add(instance);
    this.groups.set(root, group);
    instance._group = root;
  }

  static removeFromGroup(instance) {
    const group = this.groups.get(instance._group);
    if (!group) return;
    group.delete(instance);
    if (group.size === 0) this.groups.delete(instance._group);
    instance._group = null;
  }

  static observe(instance) {
    this.resizeObserver.observe(instance);
    const header = instance.header;
    if (header) {
      const count = this.observedHeaders.get(header) || 0;
      if (count === 0) this.resizeObserver.observe(header);
      this.observedHeaders.set(header, count + 1);
    }
    if (this.instances.size === 1) window.addEventListener('resize', this.onWindowResize);
  }

  static unobserve(instance) {
    this.resizeObserver.unobserve(instance);
    const header = instance.header;
    if (header) {
      const count = this.observedHeaders.get(header) || 0;
      if (count <= 1) {
        this.resizeObserver.unobserve(header);
        this.observedHeaders.delete(header);
      } else {
        this.observedHeaders.set(header, count - 1);
      }
    }
    if (this.instances.size === 0) window.removeEventListener('resize', this.onWindowResize);
  }

  get header() {
    return this._header = this._header || document.querySelector('.header-wrapper')
  }

  get level() {
    return Number(this.getAttribute('level') || 0);
  }

  get navZone() {
    const sel = this.getAttribute('nav-zone');
    if (!sel) return null;
    if (this._navZoneSelector === sel && this._navZone) return this._navZone;

    this._navZoneSelector = sel;
    try {
      this._navZone = this.closest(sel) || document.querySelector(sel) || null;
    } catch {
      this._navZone = null;
    }
    return this._navZone;
  }

  get openTrigger() {
    return this.getAttribute('open-trigger') || this.navZone?.dataset.menuOpenTrigger || 'hover';
  }

  _groupRoot() {
    return this.navZone || this.closest('nav.header__inline-menu') || document;
  }

  connectedCallback() {
    if (!this.hasAttribute('open-trigger')) {
      this.setAttribute('open-trigger', this.openTrigger);
    }

    HeaderMenu.instances.add(this);
    HeaderMenu.addToGroup(this);
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.trigger?.addEventListener('click', this.onTriggerClick);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.addEventListener('keydown', this.onInteractionKeyDown);
    HeaderMenu.observe(this);
    this.onResize();
  }

  disconnectedCallback() {
    HeaderMenu.instances.delete(this);
    HeaderMenu.removeFromGroup(this);
    HeaderMenu.unobserve(this);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('keydown', this.onInteractionKeyDown);
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    HeaderMenu.layoutQueue.delete(this);
    clearTimeout(this._hoverOpenTimer);
    clearTimeout(this._hoverCloseTimer);
  }

  _blurActiveDescendant() {
    const root = this.getRootNode();
    const active = root && root.activeElement;
    if (active && this.contains(active)) active.blur();
  }

  closePredictiveSearch() {
    const searchModals = this.header?.querySelectorAll('predictive-search');
    searchModals?.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
  }

  get trigger() {
    return this.querySelector(':scope > a, :scope > button, :scope > [role="button"]');
  }

  get submenuTemplate() {
    return this.querySelector(':scope > template.header__submenu-template');
  }

  ensureSubmenu() {
    if (this.submenu || this.megaMenu) return this.submenu || this.megaMenu;

    const template = this.submenuTemplate;
    if (!template) return null;

    template.replaceWith(template.content.cloneNode(true));
    return this.submenu;
  }

  openMenu() {
    if (!this.ensureSubmenu()) return;

    this.closePredictiveSearch();
    const myGroup = this._groupRoot();

    if (!this._isOpen) {
      this._isOpen = true;
      this.classList.add('active');
      this.trigger?.setAttribute('aria-expanded', 'true');
    }

    this._openOrder = ++HeaderMenu.openSequence;
    this.updateSubmenuPosition();
    this.updateParentLevel3Position();

    const sameLevelSiblings = [];
    const group = HeaderMenu.groups.get(myGroup) || [];
    group.forEach(inst => {
      if (inst === this) return;
      if (inst.contains(this) || this.contains(inst)) return;
      if (!inst._isOpen) return;

      if (inst.level > this.level) {
        inst.closeMenu();
        return;
      }

      if (inst.level === this.level) sameLevelSiblings.push(inst);
    });

    const maxOpenSiblings = this.level >= 3 ? 2 : 1;
    sameLevelSiblings
      .sort((a, b) => (b._openOrder || 0) - (a._openOrder || 0))
      .slice(maxOpenSiblings - 1)
      .forEach(inst => inst.closeMenu());
  }

  closeMenu() {
    const group = HeaderMenu.groups.get(this._group) || [];
    group.forEach(inst => {
      if (inst !== this && this.contains(inst)) inst.closeMenu();
    });
    if (!this._isOpen) return;
    this._isOpen = false;
    this.classList.remove('active');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.resetSubmenuPosition();
    this.updateParentLevel3Position();
  }

  toggleMenu() {
    this._isOpen ? this.closeMenu() : this.openMenu();
  }

  onPointerDown() {
    this._lastInteraction = 'pointer';
  }

  onInteractionKeyDown() {
    this._lastInteraction = 'keyboard';
  }

  onPointerEnter() {
    if (this.openTrigger === 'click') return;
    clearTimeout(this._hoverCloseTimer);
    clearTimeout(this._hoverOpenTimer);
    this._hoverOpenTimer = setTimeout(() => this.openMenu(), 60);
  }

  onPointerLeave(e) {
    if (this.openTrigger === 'click') return;
    clearTimeout(this._hoverOpenTimer);
    clearTimeout(this._hoverCloseTimer);
    const to = e.relatedTarget;

    if (to instanceof Node && this.contains(to)) return;
    this._hoverCloseTimer = setTimeout(() => {
      if (this.matches(':hover')) return;

      const root = this.getRootNode();
      const active = root && root.activeElement;
      const hasKeyboardFocus = active
        && this.contains(active)
        && active.matches?.(':focus-visible');
      if (hasKeyboardFocus) return;

      this._blurActiveDescendant();
      this.closeMenu();
    }, 80);
  }

  onFocusIn() {
    clearTimeout(this._hoverCloseTimer);
  }

  onFocusOut(e) {
    const to = e.relatedTarget;
    if (to instanceof Node && this.contains(to)) return;
    this.closeMenu();
  }

  onKeyDown(e) {
    const isTriggerToggle = e.target === this.trigger && (e.key === 'Enter' || e.key === ' ');
    if (isTriggerToggle) {
      e.preventDefault();
      this.toggleMenu();
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeMenu();
      this.trigger?.focus();
    }
  }

  onTouchStart(e) {
    this._lastInteraction = 'touch';
    this.ensureSubmenu();

    if (this.openTrigger === 'click') {
      this._openOnTouchClick = false;
      return;
    }

    const interactive = e.target.closest('a,button,[role="button"]');
    this._openOnTouchClick = interactive === this.trigger && !this._isOpen;

    if (!interactive) {
      this.toggleMenu();
    }
  }

  onTriggerClick(e) {
    if (this.openTrigger === 'click') {
      e.preventDefault();
      this.toggleMenu();
      return;
    }

    if (!this._openOnTouchClick) return;

    this._openOnTouchClick = false;
    e.preventDefault();
    this.openMenu();
  }

  onDocumentPointerDown(e) {
    if (this.openTrigger !== 'click' || !this._isOpen) return;
    if (e.target instanceof Node && this.contains(e.target)) return;
    this.closeMenu();
  }

  closeDropdown() {
    this.closeMenu();
  }

  onResize() {
    this.scheduleLayoutUpdate();
  }

  scheduleLayoutUpdate() {
    HeaderMenu.layoutQueue.add(this);
    if (HeaderMenu.layoutFrame === null) HeaderMenu.layoutFrame = requestAnimationFrame(HeaderMenu.flushLayout);
  }

  get submenu() {
    return this.querySelector(':scope > .header__submenu');
  }

  get megaMenu() {
    return this.querySelector(':scope > .mega-menu__content');
  }

  updateParentLevel3Position() {
    const parentMenu = this.parentElement?.closest('header-menu');
    const parentSubmenu = parentMenu?.submenu;

    if (!parentMenu?._isOpen || !parentSubmenu?.classList.contains('list-menu--lv3')) return;
    parentMenu.updateSubmenuPosition();
  }

  resetSubmenuPosition() {
    const submenu = this.submenu;
    if (!submenu) return;

    submenu.style.removeProperty('margin-left');
    submenu.style.removeProperty('margin-top');
    submenu.style.removeProperty('top');
    submenu.style.removeProperty('max-width');
    submenu.style.removeProperty('max-height');
    submenu.style.removeProperty('overflow-x');
    submenu.style.removeProperty('overflow-y');
  }

  updateSubmenuPosition() {
    this.scheduleLayoutUpdate();
  }

  updateSubmenuPositionNow() {
    const submenu = this.submenu;
    if (!submenu || !this._isOpen || !this.isConnected) return;

    const edge = 8;
    const bottomGap = 20;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isLevel3Submenu = submenu.classList.contains('list-menu--lv3');
    const isLevel4Submenu = submenu.classList.contains('list-menu--lv4');
    const headerHeightVar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const headerHeight = Math.max(headerHeightVar, this.header?.offsetHeight || 0);

    this.resetSubmenuPosition();

    let rect = submenu.getBoundingClientRect();
    const availableWidth = Math.max(0, viewportWidth - (edge * 2));

    if (rect.width > availableWidth) {
      submenu.style.maxWidth = availableWidth + 'px';
      rect = submenu.getBoundingClientRect();
    }

    let shiftX = 0;
    if (rect.right > viewportWidth - edge) shiftX = viewportWidth - edge - rect.right;
    if (rect.left + shiftX < edge) shiftX += edge - (rect.left + shiftX);
    if (shiftX) submenu.style.marginLeft = shiftX + 'px';

    if (isLevel3Submenu) {
      const maxHeight = Math.max(120, viewportHeight - headerHeight - bottomGap);
      const constrainedBottom = rect.top + Math.min(rect.height, maxHeight);
      submenu.style.maxHeight = maxHeight + 'px';
      submenu.style.overflowX = 'hidden';
      submenu.style.overflowY = 'auto';

      if (constrainedBottom > viewportHeight - bottomGap) {
        let shiftY = viewportHeight - bottomGap - constrainedBottom;
        if (rect.top + shiftY < headerHeight) shiftY = headerHeight - rect.top;
        if (shiftY) submenu.style.top = shiftY + 'px';
      }

      return;
    }

    if (isLevel4Submenu) return;

    const availableHeight = Math.max(120, viewportHeight - edge - Math.max(rect.top, edge));
    if (rect.bottom > viewportHeight - edge && rect.height > availableHeight) {
      submenu.style.maxHeight = availableHeight + 'px';
      submenu.style.overflowY = 'auto';
    }
  }

  computePadding() {
    this.applyPadding(this.measurePadding());
  }

  // Read phase: returns the --p-header-menu value, no style writes
  measurePadding() {
    const header = this.header;
    if (!header) return null;
    const firstChild = header.firstElementChild;
    const isTopAligned =
      firstChild?.classList.contains('header--top-center') ||
      firstChild?.classList.contains('header--top-left');

    if (isTopAligned) return 'var(--padding-bottom)';

    const headerH = Math.max(0, header.offsetHeight || 0);
    const selfH = Math.max(0, this.offsetHeight || 0);
    return `${Math.max(0, Math.floor((headerH - selfH) / 2))}px`;
  }

  applyPadding(value) {
    if (value !== null) this.style.setProperty('--p-header-menu', value);
  }
}

customElements.define('header-menu', HeaderMenu);

class ImageAnimation extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    createObserver({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    this.setAttribute('loaded', true);
    if (window.matchMedia('(hover: hover)').matches) {
      this.querySelectorAll('template[data-secondary-image]').forEach((template) => {
        template.replaceWith(template.content.cloneNode(true));
      });
    }
  }
}

customElements.define('image-animation', ImageAnimation);

class LazyloadBackground extends HTMLElement {
  constructor() {
    super();
    this._onResize = () => this.renderBackgroundImage();
  }

  get botDetected() {
    return typeof isBot === 'function' && isBot();
  }

  connectedCallback() {
    if (this.botDetected) {
      this.setAttribute('data-lazy-bg-bot-skipped', 'true');
      this.setAttribute('aria-hidden', 'true');
      return;
    }

    if (this.hasAttribute('loaded')) {
      window.addEventListener('resize', this._onResize, { passive: true });
      return;
    }

    createObserver({
      element: this,
      callback: this.init.bind(this),
      threshold: 150
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
  }

  init() {
    if (this.hasAttribute('loaded') || this.botDetected) return;
    this.renderBackgroundImage();
    this.setAttribute('loaded', '');
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  renderBackgroundImage() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const bgUrl = isMobile ? this.dataset.lazyBgMobileUrl : this.dataset.lazyBgDesktopUrl;
    if (!bgUrl) {
      this.style.removeProperty('background-image');
      return;
    }
    this.style.backgroundImage = bgUrl;
    this.style.backgroundSize = this.dataset.lazyBgSize || 'cover';
    this.style.backgroundPosition = isMobile
      ? this.dataset.lazyBgPositionMobile || this.dataset.lazyBgPosition || 'center'
      : this.dataset.lazyBgPosition || 'center';
    this.style.backgroundAttachment = this.dataset.lazyBgAttachment || 'scroll';
    this.style.backgroundRepeat = this.dataset.lazyBgRepeat || 'no-repeat';
    this.style.opacity = this.dataset.lazyBgOpacity || '1';
    this.style.mixBlendMode = this.dataset.lazyBgMixBlendMode || 'normal';
  }
}

customElements.define('lazyload-background', LazyloadBackground);

class LoadingOverlay extends HTMLElement {
  constructor() {
    super();
  }

  enableLoading() {
    this.classList.remove('hidden');
    this.querySelector('[aria-hidden]')?.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    this.classList.add('hidden');
    this.querySelector('[aria-hidden]')?.setAttribute('aria-hidden', true);
  }
}

customElements.define('loading-overlay', LoadingOverlay);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.delay = 0;
    this.animate = false;
    this.handleResize = this.handleResize.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onCloseClick = this.hide.bind(this, false);
  }

  static get observedAttributes() {
    return ['breakpoint'];
  }

  get button() {
    return this.querySelector('[id^="ModalClose-"]')
  }

  get overlay() {
    return this.querySelector('[id^="Modal-Overlay-"]')
  }

  get breakpoint() {
    return this.getAttribute('breakpoint')
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      this.elements = Array.from(document.body.children).filter(element => element !== this && element.id == this.getAttribute('id'));
      if (this.elements.length > 0) {
        this.remove();
        return;
      }
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
    }

    this.bindEvents();
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    this.unbindEvents();
    window.removeEventListener('resize', this.handleResize);
  }

  bindEvents() {
    this.addEventListener('keyup', this.onKeyUp);
    this.overlay?.addEventListener('click', this.onCloseClick);
    this.button?.addEventListener('click', this.onCloseClick);
  }

  unbindEvents() {
    this.removeEventListener('keyup', this.onKeyUp);
    this.overlay?.removeEventListener('click', this.onCloseClick);
    this.button?.removeEventListener('click', this.onCloseClick);
  }

  onKeyUp(event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key === 'Escape' || event.code === 'Escape') this.hide();
  }

  handleResize() {
    if (!this.breakpoint) return;
    if (!window.matchMedia(this.breakpoint).matches && this.hasAttribute('open')) {
      console.warn(`Modal hidden on resize: breakpoint "${this.breakpoint}" no longer matches.`);
      this.hide();
    }
  }

  loadContent() {
    const templatePopup = this.querySelector('.template-popup');
    if (!templatePopup) return;
    if (!this.getAttribute('loaded')) {
      templatePopup.replaceWith(templatePopup.content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
    }
  }

  show(triggeredBy) {
    if (this.breakpoint && !window.matchMedia(this.breakpoint).matches) {
      console.warn('Modal display suppressed due to active breakpoint.');
      return;
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.loadContent();
    this.bindEvents();
    setTimeout(() => {
      this.setAttribute('open', '');
      this.classList.add('animate', 'active');
      this.querySelector('.modal__content').scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      document.body.classList.add('modal-show', 'overflow-hidden');
      window.requestAnimationFrame(() => this.focusInitialElement());
    }, this.delay);
    document.body.dispatchEvent(new CustomEvent('modalOpened'));
    window.pauseAllMedia();
  }

  focusInitialElement() {
    if (!this.hasAttribute('open')) return;
    const dialog = this.querySelector('[role="dialog"]');
    if (!dialog) return;

    const focusTarget = dialog.querySelector('.modal__toggle:not([disabled])') || dialog;
    trapFocus(this, focusTarget);

    window.clearTimeout(this.focusRetryTimer);
    this.focusRetryTimer = window.setTimeout(() => {
      if (!this.hasAttribute('open') || this.contains(document.activeElement)) return;
      focusTarget.focus({ preventScroll: true });
    }, 100);
  }

  hide() {
    window.clearTimeout(this.focusRetryTimer);
    this.removeAttribute('open');
    this.classList.remove('animate', 'active');
    document.body.classList.remove('modal-show', 'overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    removeTrapFocus(this.activeElement);
    window.pauseAllMedia();
    window.playAllPausedMedia(this);
  }

  setActiveElement(element) {
    if(!element) return;
    this.activeElement = element;
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'breakpoint' && this.hasAttribute('open')) {
      if (!window.matchMedia(newVal).matches) {
        this.hide();
      }
    }
  }
}

customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class MoreLessLayout extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector('.more-less-layout__content');
    this.button = this.querySelector('button');
    this.readMoreText = this.getAttribute('data-read-more-text') || window.themeStrings.readMore;
    this.readLessText = this.getAttribute('data-read-less-text') || window.themeStrings.readLess;
    this.maxLinesDesktop = parseInt(this.getAttribute('data-max-lines-desktop'), 10) || 3;
    this.maxLinesMobile = parseInt(this.getAttribute('data-max-lines-mobile'), 10) || 2;
    this.expanded = false;
    this.layoutFrame = null;
    this.lastContentWidth = null;
    this.desktopMediaQuery = window.matchMedia('(min-width: 1033px)');
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width;
      if (width === undefined || width === this.lastContentWidth) return;
      this.lastContentWidth = width;
      this.scheduleLayoutUpdate();
    });
    this.onButtonClick = this.toggleReadMore.bind(this);
    this.onBreakpointChange = this.scheduleLayoutUpdate.bind(this);
    this.updateStyles();
  }

  connectedCallback() {
    this.button.addEventListener('click', this.onButtonClick);
    this.desktopMediaQuery.addEventListener('change', this.onBreakpointChange);
    this.resizeObserver.observe(this.content);
    this.scheduleLayoutUpdate();
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.onButtonClick);
    this.desktopMediaQuery.removeEventListener('change', this.onBreakpointChange);
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = null;
  }

  scheduleLayoutUpdate() {
    if (this.layoutFrame !== null) return;
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null;
      if (!this.isConnected || this.expanded) return;
      this.updateStyles();
      this.checkButtonVisibility();
    });
  }

  updateStyles() {
    this.content.style.display = '-webkit-box';
    this.content.style.overflow = 'hidden';
    this.content.style.webkitBoxOrient = 'vertical';
    this.content.style.webkitLineClamp = this.getMaxLines();
  }

  getMaxLines() {
    return this.desktopMediaQuery.matches ? this.maxLinesDesktop : this.maxLinesMobile
  }

  toggleReadMore() {
    if (this.expanded) {
      this.content.style.webkitLineClamp = this.getMaxLines();
      this.button.querySelector('span:not(.icon)').textContent = this.readMoreText;
    } else {
      this.content.style.webkitLineClamp = 'unset';
      this.button.querySelector('span:not(.icon)').textContent = this.readLessText;
    }
    this.classList.toggle('expanded', !this.expanded);
    this.expanded = !this.expanded;
  }

  checkButtonVisibility() {
    const fullLines = this.getFullLineCount();
    const maxLines = this.getMaxLines();
    const shouldClamp = fullLines > maxLines;

    this.content.style.webkitLineClamp = shouldClamp ? maxLines : "unset";
    this.button.closest(".more-less-layout__button").classList.toggle("hidden", !shouldClamp);
  }

  getFullLineCount() {
    const style = window.getComputedStyle(this.content);
    const lineHeight = parseFloat(style.lineHeight);
    const fullHeight = this.content.scrollHeight;
    return Math.round(fullHeight / lineHeight);
  }
}

customElements.define('more-less-layout', MoreLessLayout);

class ProductRecommendations extends HTMLElement {
  static instances = new Set();

  constructor() {
    super();
    this._recommendationScope = null;
  }

  connectedCallback() {
    this._recommendationScope = null;
    ProductRecommendations.instances.add(this);
    if (this.loaded) return;
    createObserver({
      element: this,
      callback: this.init.bind(this),
      threshold: 400
    });
  }

  disconnectedCallback() {
    ProductRecommendations.instances.delete(this);
    this._recommendationScope = null;
  }

  get recommendationScope() {
    return this._recommendationScope ||= this.closest('.shopify-section, cart-drawer, modal-dialog, quick-add-modal') || this.parentElement;
  }

  getCurrentRecommendations(url = null) {
    return Array.from(ProductRecommendations.instances).filter((element) => {
      if (!element.isConnected || element.recommendationScope !== this.recommendationScope) return false;
      return !url || element.dataset.url === url;
    });
  }

  static invalidateWithin(root) {
    if (!root) return;
    this.instances.forEach((element) => {
      if (root === element || root.contains?.(element)) {
        this.instances.delete(element);
        element._recommendationScope = null;
      }
    });
  }

  init() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = this.getMatchingRecommendations(html);
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.loaded = true;
        } else {
          let isHidden = this.getAttribute('data-hidden') === 'true';
          isHidden && this.remove();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getMatchingRecommendations(html) {
    const recommendations = [...html.querySelectorAll('product-recommendations')];
    if (!recommendations.length) return null;

    if (this.id) {
      const matchingById = html.getElementById(this.id);
      if (matchingById?.matches('product-recommendations')) return matchingById;
    }

    const currentUrl = this.dataset.url;
    const matchingByUrl = currentUrl ? recommendations.filter((element) => element.dataset.url === currentUrl) : [];
    if (matchingByUrl.length === 1) return matchingByUrl[0];
    if (matchingByUrl.length > 1) {
      const currentIndex = this.getCurrentRecommendations(currentUrl).indexOf(this);
      if (currentIndex > -1 && matchingByUrl[currentIndex]) return matchingByUrl[currentIndex];
    }

    const currentIndex = this.getCurrentRecommendations().indexOf(this);
    return recommendations[currentIndex] || recommendations[0];
  }
}

customElements.define('product-recommendations', ProductRecommendations);
document.addEventListener('shopify:section:unload', (event) => ProductRecommendations.invalidateWithin(event.target));

class OverflowList extends HTMLElement {
  constructor() {
    super();

    this._reflowFrameId = null;
    this.lastDimensions = { width: null, height: null };
    this.handleChange = this.handleChange.bind(this);
    this.reflowItems = this.reflowItems.bind(this);
    this.resizeObserver = new ResizeObserver(this.handleChange);
    this.mutationObserver = new MutationObserver(this.handleChange);
    this.refs = {};
  }

  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get defaultSlot() {
    return this.refs?.defaultSlot;
  }

  get overflowSlot() {
    return this.refs?.overflowSlot;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      newValue === 'true' ? this.reset() : this.requestReflow();
    }
  }

  connectedCallback() {
    if (!this.shadowRoot) this.initializeShadowRoot();
    this.collectShadowRefs();
    this.addEventListener('reflow', (event) => this.requestReflow(event.detail?.lastVisibleElement));
    this.requestReflow();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this._reflowFrameId !== null) {
      cancelAnimationFrame(this._reflowFrameId);
      this._reflowFrameId = null;
    }
  }

  initializeShadowRoot() {
    const template = this.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement)) return;
    const shadow = this.attachShadow({ mode: 'open' });
    shadow.append(template.content.cloneNode(true));
    template.remove();
  }

  collectShadowRefs(){
    const shadowRoot = this.shadowRoot;
    if (!shadowRoot) throw new Error('Missing shadow root');
    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');
    if (!(defaultSlot instanceof HTMLSlotElement) || !(overflowSlot instanceof HTMLSlotElement) || !(moreSlot instanceof HTMLSlotElement) || !(overflow instanceof HTMLElement) || !(list instanceof HTMLUListElement) || !(placeholder instanceof HTMLLIElement)) {
      throw new Error('Invalid element types in <OverflowList />');
    }
    this.refs = { defaultSlot, overflowSlot, moreSlot, overflow, list, placeholder };
  }

  handleChange(entries) {
    let width = null;
    let height = null;

    for (const entry of entries) {
      if (entry instanceof ResizeObserverEntry) {
        width = Math.round(entry.contentRect.width);
        height = Math.round(entry.contentRect.height);
      }
    }
    // Skip reflow if dimensions are 0 or the same as the last reflow
    const changed = width !== this.lastDimensions.width || height !== this.lastDimensions.height;
    if (changed) {
      this.lastDimensions = { width, height };
      this.requestReflow();
    }
  }

  requestReflow(lastVisibleElement = null) {
    if (this._reflowFrameId !== null) cancelAnimationFrame(this._reflowFrameId);
    this._reflowFrameId = requestAnimationFrame(() => {
      this._reflowFrameId = null;
      this.reflowItems(lastVisibleElement);
    });
  }

  moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.refs;
    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  reset() {
    const { list } = this.refs;
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.moveItemsToDefaultSlot();
    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;
      this.toggleAttribute('minimum-reached', minimumReached);
      this.dispatchEvent(new CustomEvent('overflow-minimum', {
        detail: { minimumReached },
        bubbles: true
      }));
    }
  }

  showAll() {
    const { placeholder } = this.refs;
    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  reflowItems(lastVisibleElement = null) {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.refs;
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};
    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');
    // Move all elements to the default slot so we can check which ones overflow
    this.moveItemsToDefaultSlot();
    const elements = defaultSlot.assignedElements();
    moreSlot.hidden = true;
    placeholder.hidden = true;
    const listRect = list.getBoundingClientRect();
    // Store the initial dimensions for comparison later
    this.lastDimensions = { width: Math.round(listRect.width), height: Math.round(listRect.height) };
    const getVisibleElements = () => {
      return elements.filter(element => {
        const rect = element.getBoundingClientRect();
        return rect.top <= listRect.top + 1
      });
    };
    let visibleElements = getVisibleElements();
    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length < elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);
      if (lastVisibleElement) lastVisibleElement.style.setProperty('order', '-1');
      // Recalculate the visible elements
      visibleElements = getVisibleElements();
      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) lastVisibleElement.style.removeProperty('order');
      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement && visibleElements.includes(lastVisibleElement)) {
        visibleElements = visibleElements.filter(element => element !== lastVisibleElement);
        visibleElements.push(lastVisibleElement);
      }
      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter(element => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;
    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }
    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;
    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');
    // Check if the minimum-reached state should be updated
    hasOverflow && this.updateMinimumReached(visibleElements);
    // Observe the list for changes in size
    this.resizeObserver.observe(this);
    this.mutationObserver.observe(this, { childList: true });
  }
}

customElements.define('overflow-list', OverflowList);

function announceCartAdd(cart, response) {
  const cartContent = cart?.querySelector('[data-cart-items]');
  const itemCount = parseInt(cartContent?.dataset.cartItems, 10);
  const responseItems = Array.isArray(response?.items) ? response.items : [];
  const addedItem = responseItems[responseItems.length - 1] || response;
  const productTitle = addedItem?.product_title || addedItem?.title;
  if (Number.isNaN(itemCount) || !productTitle) return;

  let message;
  if (responseItems.length > 1) {
    // The main product is sent last, so reverse to read it first.
    const productTitles = [...new Set(responseItems.map((item) => item.product_title || item.title).filter(Boolean).reverse())];
    const productList = new Intl.ListFormat(document.documentElement.lang || 'en', { type: 'conjunction' }).format(productTitles);
    message = window.cartStrings.addSuccessMultiple
      .replace('[products]', productList)
      .replace('[count]', itemCount);
  } else {
    const messageFormat = itemCount === 1
      ? window.cartStrings.addSuccessOne
      : window.cartStrings.addSuccessOther;
    message = messageFormat
      .replace('[product]', productTitle)
      .replace('[count]', itemCount);
  }

  if (!setCartDialogMessage(cart, message)) {
    const statusRegion = document.getElementById('CartAddStatus');
    if (statusRegion) statusRegion.textContent = message;
  }
}

// The cart drawer is aria-modal and takes focus when it opens, so the message goes into
// the dialog's name and is read when focus lands on it. CartDrawer.close() restores it.
function setCartDialogMessage(cart, message) {
  const dialog = cart?.querySelector('[role="dialog"]');
  if (!dialog) return false;

  dialog.dataset.defaultLabel ??= dialog.getAttribute('aria-label');
  dialog.setAttribute('aria-label', `${dialog.dataset.defaultLabel}. ${message}`);
  if (cart.classList.contains('active')) dialog.focus();
  return true;
}

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.form.querySelector('[name=id]').disabled = false;
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.refreshSubtotal = this.refreshSubtotal.bind(this);
    this.quantityChangeUnsubscriber = null;
    this.cart = document.querySelector('cart-drawer');
    this.hideAlert = this.dataset.hideAlert === 'true';
    this.stopState = false;
    this.keepText = this.submitButton.getAttribute('data-keep-text') === 'true';
  }

  connectedCallback() {
    this.form.addEventListener('submit', this.onSubmitHandler);
    document.body.addEventListener('subtotal:refresh', this.refreshSubtotal);
    this.quantityChangeUnsubscriber ||= subscribe(PUB_SUB_EVENTS.quantityChange, this.onQuantityChange);
  }

  disconnectedCallback() {
    this.form.removeEventListener('submit', this.onSubmitHandler);
    document.body.removeEventListener('subtotal:refresh', this.refreshSubtotal);
    this.quantityChangeUnsubscriber?.();
    this.quantityChangeUnsubscriber = null;
  }

  setCartActiveElementSafely(element = document.activeElement) {
    if (!this.cart || typeof this.cart.setActiveElement !== 'function') return;
    this.cart.setActiveElement(element);
  }

  onQuantityChange(event) {
    let _form = event.form;
    if(!_form) return;
    if (_form.getAttribute('id') !== this.form.getAttribute('id')) return;
    this.refreshSubtotal(event.value);
  }

  // Total price (in cents) of the checked add-ons that the main add button will add to the cart
  getAddOnsSubtotal() {
    const addOnProducts = this.closest('product-info')?.querySelector('add-on-products');
    if (!addOnProducts) return 0;
    const addOnAfterAddBtn = (this.submitButton.compareDocumentPosition(addOnProducts) & Node.DOCUMENT_POSITION_FOLLOWING) > 0;
    if (addOnAfterAddBtn) return 0;
    return [...addOnProducts.querySelectorAll('[name="id"]:checked:not(:disabled)')]
      .reduce((total, item) => total + Math.round((parseFloat(item.dataset.price) || 0) * 100), 0);
  }

  refreshSubtotal(quantity) {
    if (!this.submitButton.hasAttribute('data-subtotal') || this.submitButton.hasAttribute('disabled')) return;
    if (typeof quantity !== 'number' && typeof quantity !== 'string') {
      quantity = new FormData(this.form).get('quantity');
    }
    quantity = parseInt(quantity, 10);
    quantity = Number.isNaN(quantity) ? 1 : quantity;
    const submitButtonText = this.submitButton.querySelector('span:not(.svg-wrapper)');
    const subtotal = quantity * parseInt(this.submitButton.dataset.price) + this.getAddOnsSubtotal();
    const subtotal_format = Shopify.formatMoney(subtotal / 100, window.shopMoney.money_format);
    submitButtonText.innerHTML = window.variantStrings.addToCartWithSubtotal.replace('[subtotal]', subtotal_format);
    this.submitButton.setAttribute('data-subtotal', subtotal);
    this.submitButton.setAttribute('data-subtotal-format', subtotal_format);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    const addOnProducts = this.closest('product-info')?.querySelector('add-on-products');
    let products = [];
    if (addOnProducts){
      const addOnAfterAddBtn = this.submitButton && (this.submitButton.compareDocumentPosition(addOnProducts) & Node.DOCUMENT_POSITION_FOLLOWING) > 0;
      if(addOnAfterAddBtn) {
        this.addToCart();
        return;
      }
      if(addOnProducts.modal) {
        if(!addOnProducts.checkSimpleProducts()) {
          products = addOnProducts.allProducts();
          if(products.length > 0) {
            addOnProducts.openModal(this.submitButton);
            return;
          }
        }
      }
      products = addOnProducts.allProducts();
    }
    if (products.length > 0) {
      this.addAllToCart(products);
    } else {
      this.addToCart();
    }
  }

  async addFreeGift() {
    try {
      const variantId = this.closest('product-info')?.querySelector('[data-free-gift]')?.dataset.variantId;
      if (!variantId) return;
      const quantity = parseInt(new FormData(this.form).get('quantity'), 10) || 1;
      const body = JSON.stringify({ items: [{ id: variantId, quantity }] });
      await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body });
    } catch (error) {
      console.error(error);
    }
  }

  addToCart() {
    this.error = false;
    this.stopState = false;
    this.handleButton('adding');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    const formData = new FormData(this.form);
    const cartItemCountBeforeAdd = this.getCartItemCount();
    if (this.cart) {
      formData.append('sections_url', window.location.pathname);
      this.setCartActiveElementSafely();
    }
    config.body = formData;
    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const errorMessage = response.description || response.message;
          publish(PUB_SUB_EVENTS.cartError, {
            source: this,
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: errorMessage
          });
          this.handleErrorMessage(errorMessage);
          this.handleButton('add');
          this.error = true;
          this.stopState = true;
          this.syncCartAfterError(cartItemCountBeforeAdd, formData.get('id'), errorMessage);
          return;
        }
        await this.addFreeGift();
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: this,
            cartData: response,
            productVariantId: formData.get('id'),
            type: 'add'
          });
        }
        this.error = false;
        const quickModal = this.closest('modal-dialog') || this.closest('quick-add-modal');
        const quickDrawer = this.closest('side-drawer');
        if (quickModal) quickModal.hide();
        if (quickDrawer) quickDrawer.close();
        if (this.cart) {
          document.body.addEventListener('cartRendered', () => {
            this.announceAddToCart(response);
            setTimeout(() => this.cart.open());
          }, { once: true });
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.handleButton('added', false);
      });
  }

  getCartItemCount() {
    const cartContent = this.cart?.querySelector('[data-cart-items]');
    if (!cartContent) return null;

    const itemCount = parseInt(cartContent.dataset.cartItems, 10);
    return Number.isNaN(itemCount) ? null : itemCount;
  }

  announceAddToCart(response) {
    announceCartAdd(this.cart, response);
  }

  syncCartAfterError(previousItemCount, productVariantId, errorMessage) {
    if (!this.cart) return;

    const stopWaitingForCart = setTimeout(() => {
      document.body.removeEventListener('cartRendered', handleCartRendered);
    }, 5000);
    const handleCartRendered = () => {
      const currentItemCount = this.getCartItemCount();
      const cartWasUpdated = previousItemCount !== null
        && currentItemCount !== null
        && currentItemCount > previousItemCount;
      if (!cartWasUpdated) return;

      clearTimeout(stopWaitingForCart);
      document.body.removeEventListener('cartRendered', handleCartRendered);

      const quickModal = this.closest('modal-dialog') || this.closest('quick-add-modal');
      const quickDrawer = this.closest('side-drawer');
      if (quickModal) quickModal.hide();
      if (quickDrawer) quickDrawer.close();

      setTimeout(() => {
        // The drawer takes focus, so the error is read from its label; the alert is visual only.
        const announcedInCart = setCartDialogMessage(this.cart, errorMessage);
        if (typeof this.cart.open === 'function') this.cart.open();
        this.handleErrorMessage(errorMessage, 6000, !announcedInCart);
      });
    };
    document.body.addEventListener('cartRendered', handleCartRendered);

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: this,
      cartData: null,
      productVariantId,
      type: 'error-sync'
    });
  }

  addAllToCart(items) {
    const mainQuantity = parseInt(new FormData(this.form).get('quantity'), 10) || 1;
    const products = {
      items: [
        ...items.map((element) => ({ id: element, quantity: 1 })),
        { id: this.form.querySelector('[name=id]').value, quantity: mainQuantity }
      ]
    };
    this.error = false;
    this.stopState = false;
    this.handleButton('adding');
    let body;
    if (this.cart) {
      body = JSON.stringify({
        ...products,
        sections_url: window.location.pathname
      });
      this.setCartActiveElementSafely();
    } else {
      body = JSON.stringify({
        ...products
      });
    }
    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: this,
            errors: response.errors || response.description,
            message: response.message
          });
          this.handleErrorMessage(response.description);
          this.handleButton('add');
          this.error = true;
          return;
        }
        await this.addFreeGift();
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: this,
            cartData: response,
            type: 'bundle'
          });
        }
        this.error = false;
        const quickModal = this.closest('modal-dialog') || this.closest('quick-add-modal');
        const quickDrawer = this.closest('side-drawer');
        if (quickModal) quickModal.hide();
        if (quickDrawer) quickDrawer.close();
        document.body.addEventListener('cartRendered', () => {
          this.announceAddToCart(response);
          setTimeout(() => this.cart.open());
        }, { once: true });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.handleButton('added', false);
      });
  }

  handleButton(state = 'adding', disabled = false) {
    if(this.stopState) return;
    const submitButtonText = this.submitButton.querySelector('span:not(.svg-wrapper)');
    switch (state) {
      case 'add':
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        submitButtonText.textContent = window.variantStrings.addToCart;
        break;
      case 'adding':
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (!this.keepText) {
          submitButtonText.textContent = window.variantStrings.addingToCart;
        }
        break;
      case 'added':
        this.submitButton.setAttribute('aria-disabled', true);
  
        if (disabled) {
          submitButtonText.textContent = window.variantStrings.soldOut;
        } else {
          if (!this.keepText) {
            submitButtonText.textContent = window.variantStrings.addedToCart;
          }
          setTimeout(() => {
            this.submitButton.classList.remove('loading');
            if (!this.keepText) {
              if(this.submitButton.hasAttribute('data-subtotal')) {
                this.refreshSubtotal();
              } else {
                submitButtonText.textContent = window.variantStrings.addToCart;
              }
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
          }, 500);
        }
        break;
      default:
        break;
    }
  }

  handleSuccessMessage(successMessage = false) {
    if (this.hideAlert || successMessage == false) return;
    this.successAlert = this.successAlert || this.querySelector(`[id^="Alert-Success-"]`) || document.querySelector(`[id="Alert-Success"]`);
    if (!this.successAlert) return;
    this.successAlert.show(successMessage, { type: 'success', duration: ON_VISIBLE_DEBOUNCE_TIMER });
  }

  handleErrorMessage(errorMessage = false, duration = ON_VISIBLE_DEBOUNCE_TIMER, announce = true) {
    if (this.hideAlert || errorMessage == false) return;
    this.errorAlert = this.errorAlert || this.querySelector(`[id^="Alert-Error-"]`) || document.querySelector(`[id="Alert-Error"]`);
    if (!this.errorAlert) return;
    this.errorAlert.show(errorMessage, { type: 'error', duration, announce });
  }
}

customElements.define('product-form', ProductForm);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    this.onInputChange = this.onInputChange.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.validateQtyRules = this.validateQtyRules.bind(this);
    this.quantityUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.input.addEventListener('change', this.onInputChange);
    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick));
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber ||= subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules);
  }

  disconnectedCallback() {
    this.input.removeEventListener('change', this.onInputChange);
    this.buttons.forEach((button) => button.removeEventListener('click', this.onButtonClick));
    this.quantityUpdateUnsubscriber?.();
    this.quantityUpdateUnsubscriber = null;
  }

  onInputChange(event) {
    this.roundToQtyRules();
    this.validateQtyRules();
  }

  // Snaps a typed quantity to the nearest value the quantity rule allows (min + n * step, up to max),
  // instead of leaving it to the browser's "enter a valid value" message. Halves round up (6 -> 8 for 4/4).
  roundToQtyRules() {
    const step = parseInt(this.input.step, 10);
    if (!(step > 0)) return;
    const attrMin = parseInt(this.input.min, 10);
    const ruleMin = parseInt(this.input.dataset.min, 10);
    // Cart lines use min="0" so 0 can remove the line; the rule's own min comes from data-min
    const allowZero = attrMin === 0;
    let base = allowZero ? ruleMin : attrMin;
    if (!(base > 0)) base = step;
    const max = parseInt(this.input.max, 10);
    if (!Number.isNaN(max) && max < base) return;

    let value = parseInt(this.input.value, 10);
    if (Number.isNaN(value)) {
      if (allowZero) return;
      value = base;
    }
    if (allowZero && value <= 0) {
      this.input.value = 0;
      return;
    }

    let rounded = Math.max(base, base + Math.round((value - base) / step) * step);
    if (!Number.isNaN(max) && rounded > max) rounded = base + Math.floor((max - base) / step) * step;
    this.input.value = rounded;
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }

    publish(PUB_SUB_EVENTS.quantityChange, {
      source: this,
      form: this.input.form,
      value: value
    });
  }
}

customElements.define('quantity-input', QuantityInput);

class ProductConfiguration extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onChange.bind(this));
  }

  onChange(event) {
    if (this.closest('quick-add-modal')) return;
    const input = event.target.closest('[data-product-url]');
    if (!input || !input.checked) return;
    window.location.href = input.dataset.productUrl;
  }
}

customElements.define('product-configuration', ProductConfiguration);

class QuickAddModal extends ModalDialog {
  constructor() {
    super();
    this.moved = true;
    this.loadingOverlay = this.querySelector('[id^="LoadingOverlay"]');
    this.modalContent = this.querySelector('[id^="QuickAddInfoModal"]');
    this.ratingSummaryObserver = new MutationObserver(() => this.disableQuickAddRatingFocus());
    this.ratingSummaryObserver.observe(this.modalContent, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex', 'href']
    });
    this.addEventListener('change', this.onConfigurationChange.bind(this));
  }

  disableQuickAddRatingFocus() {
    const focusableSelector = 'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';
    this.modalContent
      ?.querySelectorAll('.product.product--quick-add [data-bv-show="rating_summary"]')
      .forEach((ratingSummary) => {
        const focusableElements = ratingSummary.matches(focusableSelector)
          ? [ratingSummary, ...ratingSummary.querySelectorAll(focusableSelector)]
          : ratingSummary.querySelectorAll(focusableSelector);
        focusableElements.forEach((element) => {
          if (element.getAttribute('tabindex') !== '-1') element.setAttribute('tabindex', '-1');
        });
      });
  }

  show(opener) {
    opener.setAttribute('aria-disabled', true);
    const parentDialog = opener.closest('.modal');
    if (parentDialog) parentDialog.hide();
    super.show(opener);
    this.loadProduct(opener.getAttribute('data-product-url'))
      .finally(() => {
        opener.removeAttribute('aria-disabled');
        trapFocus(this, this.querySelector('[role="dialog"]'));
        window.pauseAllMedia();
      });
  }

  onConfigurationChange(event) {
    const input = event.target.closest('[data-quick-add-product-url]');
    if (!input || !input.checked) return;
    const configurationBlock = input.closest('.product__configuration');
    const fallbackConfiguration = configurationBlock?.cloneNode(true);
    const selectedLabel = configurationBlock?.querySelector(`label[for="${CSS.escape(input.id)}"] span:last-child`);

    this.loadProduct(input.dataset.quickAddProductUrl)
      .then(() => {
        if (!this.querySelector('.product__configuration') && fallbackConfiguration) {
          fallbackConfiguration.querySelectorAll('[data-quick-add-product-url]').forEach((configurationInput) => {
            configurationInput.checked = configurationInput.value === input.value;
          });
          const selectedConfiguration = fallbackConfiguration.querySelector('[data-selected-configuration]');
          if (selectedConfiguration && selectedLabel) selectedConfiguration.textContent = selectedLabel.textContent.trim();

          const productInfo = this.querySelector('product-info');
          const nextBlock = productInfo?.querySelector('.product__variant, .product__action');
          if (nextBlock) {
            nextBlock.before(fallbackConfiguration);
          } else if (productInfo) {
            productInfo.appendChild(fallbackConfiguration);
          }
        }

        const activeConfiguration = this.querySelector('[data-quick-add-product-url]:checked');
        if (activeConfiguration) activeConfiguration.focus({ preventScroll: true });
        window.pauseAllMedia();
      });
  }

  loadProduct(url) {
    if (!url) return Promise.reject(new Error('Quick add product URL is missing.'));
    const productUrl = new URL(url, window.location.origin);
    productUrl.searchParams.set('view', 'quick-add');
    this.enableLoading();
    return fetch(productUrl.toString())
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Quick add request failed with status ${response.status}.`);
        }
        return response;
      })
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.productElement = responseHTML.querySelector('[id^="MainProduct-"]');
        if (!this.productElement) throw new Error('Quick add product content was not found.');
        // Keep the content hidden until its stylesheets are applied to avoid an unstyled layout flash.
        this.modalContent.style.visibility = 'hidden';
        injectHTMLWithScripts(this.modalContent, this.productElement.innerHTML);
        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        return this.waitForStylesheets();
      })
      .then(() => {
        this.classList.add('has-content');
      })
      .finally(() => {
        this.modalContent.style.visibility = '';
        this.disableLoading();
      });
  }

  waitForStylesheets(timeout = 3000) {
    const pending = Array.from(this.modalContent.querySelectorAll('link[rel="stylesheet"]'))
      .filter((link) => !link.sheet)
      .map(
        (link) =>
          new Promise((resolve) => {
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
          })
      );
    if (!pending.length) return Promise.resolve();
    // Never block the popup forever if a stylesheet hangs.
    return Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, timeout))]);
  }

  hide(preventFocus = false) {
    if (preventFocus) this.activeElement = null;
    super.hide();
    injectHTMLWithScripts(this.modalContent, '');
    this.classList.remove('has-content');
  }

  enableLoading() {
    this.loadingOverlay.enableLoading();
  }

  disableLoading() {
    this.loadingOverlay.disableLoading();
  }
}

customElements.define('quick-add-modal', QuickAddModal);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    createObserver({
      element: this,
      callback: this.init.bind(this),
      threshold: 400
    });
  }

  init() {
    this.list = readCookie('shopify_recently_viewed_products');
    if(this.list.length === 0) {
      this.checkContent();
      return
    }
    this.loadContent();
  }

  getQueryString(list) {
    if (this.dataset.productId && list.includes(parseInt(this.dataset.productId))) list.splice(list.indexOf(parseInt(this.dataset.productId)), 1);
    return list.map((item) => "id:" + item).slice(0, 10).join(" OR ");
  }

  checkContent(html = null){
    this.setAttribute('loaded', true);
    this.products = html ? Array.from(html.querySelectorAll('[data-product-item]')) : [];
    if(this.products.length === 0) {
      this.remove();
    }
  }

  loadContent() {
    if (!this.getAttribute('loaded')) {
      fetch(this.dataset.url + this.getQueryString(this.list))
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const products = html.querySelector('recently-viewed-products');
          if (products && products.innerHTML.trim().length) this.innerHTML = products.innerHTML;
          this.checkContent(html);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);

class ScrollShadow extends HTMLElement {
  static updaters = new WeakMap();

  static Updater = class {
    constructor(targetElement) {
      const update = this.update.bind(this, targetElement, getComputedStyle(targetElement));
      this.handleScroll = this.throttle(update);
      this.resizeObserver = new ResizeObserver(update);
    }

    clamp(num, min = 0, max) {
      if (num < min) return min;
      if (num > max) return max;
      return num;
    }
    
    throttle(callback) {
      let id = null;
      return () => {
        if (id) return;
        id = requestAnimationFrame(() => {
          callback();
          id = null;
        });
      };
    }
  
    start(element, rootElement) {
      if (this.element) this.stop();
      if (element) {
        element.addEventListener('scroll', this.handleScroll);
        this.resizeObserver.observe(element);
        this.element = element;
      }
      if (rootElement) {
        this.resizeObserver.observe(rootElement);
        this.rootElement = rootElement;
      }
    }
  
    stop() {
      if (!this.element) return;
      this.element.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver.disconnect();
      this.element = null;
      this.rootElement = null;
    }
  
    update(targetElement, computedStyle) {
      const { element, rootElement } = this;
      if (!element) return;
      const maxSize = Number(computedStyle.getPropertyValue('--scroll-shadow-size') || 14);
      const style = {
        '--top': this.clamp(element.scrollTop, 0, maxSize),
        '--bottom': this.clamp(element.scrollHeight - element.offsetHeight - element.scrollTop, 0, maxSize),
        '--left': this.clamp(element.scrollLeft, 0, maxSize),
        '--right': this.clamp(element.scrollWidth - element.offsetWidth - element.scrollLeft, 0, maxSize),
      };
  
      if (rootElement) {
        const clientRect = element.getBoundingClientRect();
        const rootClientRect = rootElement.getBoundingClientRect();
        Object.assign(style, {
          top: this.clamp(clientRect.top - rootClientRect.top),
          bottom: this.clamp(rootClientRect.bottom - clientRect.bottom),
          left: this.clamp(clientRect.left - rootClientRect.left),
          right: this.clamp(rootClientRect.right - clientRect.right),
        });
      }
  
      for (const key in style) {
        targetElement.style.setProperty(key, `${style[key]}px`);
      }
    }
  };

  constructor() {
    super();
    this.template = `
      <style>
      :host {
        display: inline-block;
        position: relative;
      }
      :host([hidden]) {
        display: none;
      }
      s {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        z-index: var(--z-index-temporary, 5);
        background:
          var(--scroll-shadow-top, radial-gradient(farthest-side at 50% 0%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) top/100% var(--top),
          var(--scroll-shadow-bottom, radial-gradient(farthest-side at 50% 100%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) bottom/100% var(--bottom),
          var(--scroll-shadow-left, radial-gradient(farthest-side at 0%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) left/var(--left) 100%,
          var(--scroll-shadow-right, radial-gradient(farthest-side at 100%, rgba(var(--color-foreground),.2), rgba(var(--color-foreground),0))) right/var(--right) 100%;
        background-repeat: no-repeat;
      }
      </style>
      <slot></slot>
      <s></s>
    `;
    this.attachShadow({ mode: 'open' }).innerHTML = this.template;
    ScrollShadow.updaters.set(this, new ScrollShadow.Updater(this.shadowRoot.lastElementChild));
  }

  static get observedAttributes() {
    return ['element'];
  }

  get element() {
    return this.getAttribute('element');
  }

  set element(value) {
    this.setAttribute('element', value);
  }

  connectedCallback() {
    this.shadowRoot.querySelector('slot').addEventListener('slotchange', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    ScrollShadow.updaters.get(this).stop();
  }

  start() {
    const element = this.scrollElement || this.firstElementChild;
    ScrollShadow.updaters.get(this).start(element, this.scrollElement ? this.firstElementChild : null);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.scrollElement = newValue ? this.querySelector(newValue) : null;
      this.start();
    }
  }
}

customElements.define('scroll-shadow', ScrollShadow);

class ScrollTopButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const buttons = this.querySelectorAll('button');
    [...buttons].forEach((button) => button.addEventListener('click', this.handleScrollTop.bind(this)));
  }

  disconnectedCallback() {
    // code here
  }

  handleScrollTop(event) {
    event.preventDefault();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }
}

customElements.define('scroll-top-button', ScrollTopButton);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.onFormReset = this.onFormReset.bind(this);
    this.onChange = this.onChange.bind(this);
    this.inputHandler = debounce(this.onChange, ON_CHANGE_DEBOUNCE_TIMER);
  }

  connectedCallback() {
    this.inputSearch = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (!this.inputSearch) return;
    this.form = this.inputSearch.form;
    if (this.form) this.form.addEventListener('reset', this.onFormReset);
    this.inputSearch.addEventListener('input', this.inputHandler);
    this.toggleResetButton();
  }

  disconnectedCallback() {
    if (this.inputSearch) this.inputSearch.removeEventListener('input', this.inputHandler);
    if (this.form) this.form.removeEventListener('reset', this.onFormReset);
  }

  toggleResetButton() {
    if (!this.resetButton || !this.inputSearch) return;
    const hasValue = this.inputSearch.value.length > 0;
    this.resetButton.classList.toggle('hidden', !hasValue);
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a')
  }

  onFormReset(event) {
    event.preventDefault();
    if (!this.inputSearch) return;

    if (this.shouldResetForm()) {
      this.inputSearch.value = '';
      this.inputSearch.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    button.addEventListener('click', this.expandShowMore.bind(this));
  }

  expandShowMore(event) {
    const button = event.currentTarget;
    const parent = button.closest('.parent-wrap');
    button.classList.toggle('show');
    button.querySelector('span').innerText = button.classList.contains('show') ? button.dataset.showLessText : button.dataset.showMoreText;
    parent.querySelectorAll('.show-more-item').forEach(item => item.classList.toggle('hidden'))
  }
}

customElements.define('show-more-button', ShowMoreButton);

class MetafieldsShowMore extends HTMLElement {
  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    const itemsToShow = parseInt(this.dataset.itemsToShow, 10) || 6;

    let container = this;
    while (container.children.length === 1) {
      container = container.children[0];
    }

    const items = [];
    Array.from(container.children).forEach(child => {
      if (child.tagName === 'UL' || child.tagName === 'OL') {
        items.push(...child.children);
      } else {
        items.push(child);
      }
    });
    if (items.length <= itemsToShow) return;

    const hiddenItems = items.slice(itemsToShow);
    hiddenItems.forEach(item => (item.hidden = true));

    const showMoreText = this.dataset.showMoreText || window.themeStrings.showMore;
    const showLessText = this.dataset.showLessText || window.themeStrings.showLess;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'show-more-button__button link link--hover link--text uppercase';
    button.innerHTML = `<span>${showMoreText}</span>`;
    button.addEventListener('click', () => {
      const expanded = button.classList.toggle('show');
      hiddenItems.forEach(item => (item.hidden = !expanded));
      button.querySelector('span').textContent = expanded ? showLessText : showMoreText;

      if (!expanded) {
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const top = this.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'show-more-button block text-center no-js-hidden';
    wrapper.appendChild(button);
    this.appendChild(wrapper);
  }
}

customElements.define('metafields-show-more', MetafieldsShowMore);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  get overlay() {
    return this.querySelector('[id^="Drawer-Overlay-"]');
  }

  connectedCallback() {
    this.bindEvents();
    if (this.moved) return;
    this.moved = true;
    this.elements = Array.from(document.body.children).filter(element => element.id == this.getAttribute('id'));
    this.elements.length > 0 ? this.remove() : document.body.appendChild(this);
  }

  lockScroll() {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    document.documentElement.dataset.scrollY = scrollY;
    document.documentElement.style.scrollBehavior = 'auto';
    document.documentElement.classList.add('is-locked');
    document.documentElement.style.top = `-${scrollY}px`;
  }
  
  unlockScroll() {
    const scrollY = parseInt(document.documentElement.dataset.scrollY || '0', 10);
    document.documentElement.classList.remove('is-locked');
    document.documentElement.style.top = '';
    window.scrollTo(0, scrollY);
    document.documentElement.style.scrollBehavior = '';
  }

  bindEvents() {
    this.overlay.addEventListener('click', this.close.bind(this));
  }

  loadContent() {
    const templateDrawer = this.querySelector('.template-drawer');
    if (!templateDrawer) return;
    if (!this.getAttribute('loaded')) {
      templateDrawer.replaceWith(templateDrawer.content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if(this.activeElement) this.activeElement.setAttribute('open', '');
    this.loadContent();
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this;
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    document.body.classList.add('drawer-show');
    this.lockScroll();
  }

  close() {
    this.classList.remove('animate', 'active');
    if(this.activeElement) this.activeElement.removeAttribute('open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('drawer-show');
    this.unlockScroll();
  }

  setActiveElement(element) {
    if (!element) return;
    if (this.classList.contains('active') && this.activeElement?.isConnected) return;

    this.activeElement = element;
  }
}

customElements.define('side-drawer', SideDrawer);

class SideDrawerOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick(event) {
    let _target = event.currentTarget;
    const drawer = document.querySelector(this.getAttribute('data-side-drawer'));
    drawer && (_target.hasAttribute('open') ? drawer.close() : drawer.open(_target));
  }
}

customElements.define('side-drawer-opener', SideDrawerOpener);

class StickyElement extends HTMLElement {
  static shownElements = new Map();
  static resizeObserver = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => target.onResize());
  });

  static updateBody(type) {
    const shown = StickyElement.shownElements.get(type);
    let height = 0;
    shown?.forEach((element) => {
      height = Math.max(height, element.stickyHeight);
    });
    document.body.classList.toggle(`${type}-sticky__show`, Boolean(shown?.size));
    document.body.style.setProperty(`--${type}-sticky-height`, `${height}px`);
  }

  constructor() {
    super();
    this.onScrollHandler = this.onScroll.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.scrollFrame = null;
    this.isShown = null;
    this._stickyHeight = null;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
  }

  get section() {
    return this._section || (this._section = this.closest('.shopify-section'))
  }

  get type() {
    return this._type = this._type || this.dataset.type
  }

  get anchor() {
    return this._anchor || (this._anchor = this.section?.querySelector(this.dataset.anchor))
  }

  get threshold() {
    const threshold = Number.parseFloat(this.dataset.threshold);
    return Number.isFinite(threshold) ? threshold : 100
  }

  get mode() {
    return this._mode || (this._mode = this.dataset.mode === 'conceal' ? 'conceal' : 'reveal')
  }

  get mainContent() {
    return this._mainContent || (this._mainContent = document.getElementById('MainContent'))
  }

  get stickyHeight() {
    if (this._stickyHeight === null) this.measure();
    return this._stickyHeight
  }

  connectedCallback() {
    this.isShown = null;
    this._stickyHeight = null;
    this.classList.remove('hidden');
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    StickyElement.resizeObserver.observe(this);
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
    StickyElement.resizeObserver.unobserve(this);
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = null;
    if (this.isShown) {
      StickyElement.shownElements.get(this.type)?.delete(this);
      StickyElement.updateBody(this.type);
    }
    this.isShown = null;
  }

  measure() {
    let height = this.offsetHeight;
    if (this.type === 'sticky-product') height += Number.parseFloat(getComputedStyle(this).bottom) || 0;
    this._stickyHeight = Math.floor(height);
  }

  onResize() {
    this.measure();
    if (this.isShown) StickyElement.updateBody(this.type);
  }

  onBreakpointChange() {
    this.onResize();
    this.onScroll();
  }

  onScroll() {
    if (this.scrollFrame !== null) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.isConnected) return;
      // The product sticky bar is mobile only.
      if (this.type === 'product' && !this.mobileQuery.matches) {
        this.hide();
        return
      }
      if (this.mode === 'conceal') {
        // Shown by default; hidden only while the anchor is visible on screen.
        if (this.anchor && this.isInView(this.anchor)) {
          this.hide();
        } else {
          this.show();
        }
      } else if (this.anchor && this.isPastThreshold(this.anchor, this.threshold)) {
        if (this.type === 'sticky-product' && this.mainContent && this.isPastElement(this.mainContent)) {
          this.hide();
        } else {
          this.show();
        }
      } else {
        this.hide();
      }
    });
  }

  isPastThreshold(element, threshold = 0) {
    return element.getBoundingClientRect().y + threshold < 0
  }

  isInView(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight
  }

  isPastElement(element) {
    return element.getBoundingClientRect().bottom <= window.innerHeight - this.stickyHeight
  }


  hide() {
    this.setShown(false);
  }

  show() {
    this.setShown(true);
  }

  setShown(shown) {
    if (this.isShown === shown) return;
    this.isShown = shown;
    this.classList.toggle('animate', shown);
    this.classList.toggle('active', shown);
    const { shownElements } = StickyElement;
    if (!shownElements.has(this.type)) shownElements.set(this.type, new Set());
    shownElements.get(this.type)[shown ? 'add' : 'delete'](this);
    StickyElement.updateBody(this.type);
  }
}

customElements.define('sticky-element', StickyElement);

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.section-header');
    this.stickyType = this.getAttribute('data-sticky-type');
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.isScrolling = null;
    this.onScrollHandler = this.onScroll.bind(this);
    this.scrollFrame = null;
    this.heightFrame = null;
    this.revealTimer = null;
    this.observer = null;
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;
    this.onResize = debounce(this.setHeaderHeight.bind(this), 100);
    this.exitTimer = null;
    this.onEnterHandler = this.onEnter.bind(this);
    this.onLeaveHandler = this.onLeave.bind(this);
  }

  connectedCallback() {
    this.setHeaderHeight();
    window.addEventListener('resize', this.onResize);
    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    this.addEventListener('mouseenter', this.onEnterHandler);
    this.addEventListener('mouseleave', this.onLeaveHandler);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.createObserver();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    this.removeEventListener('mouseenter', this.onEnterHandler);
    this.removeEventListener('mouseleave', this.onLeaveHandler);
    window.removeEventListener('scroll', this.onScrollHandler);
    this.observer?.disconnect();
    this.observer = null;
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.heightFrame);
    this.scrollFrame = null;
    this.heightFrame = null;
    clearTimeout(this.exitTimer);
    clearTimeout(this.isScrolling);
    clearTimeout(this.revealTimer);
    this.revealTimer = null;
  }

  onEnter() {
    this.preventClose = true;
    this.header.classList.add('is-active');
    clearTimeout(this.exitTimer);
  }

  onLeave() {
    this.exitTimer = setTimeout(() => this.header.classList.remove('is-active'), 400);
  }

  createObserver() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(([entry]) => {
      this.headerBounds = entry.intersectionRect;
      if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const { top, bottom } = entry.boundingClientRect;
        this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
      }
      this.observer?.disconnect();
      this.observer = null;
      this.onScroll();
    });

    this.observer.observe(this.header);
  }

  setHeaderHeight() {
    if (this.heightFrame !== null) return;
    this.heightFrame = requestAnimationFrame(() => {
      this.heightFrame = null;
      if (!this.isConnected) return;
      // Root custom property: every write restyles the whole page, skip unchanged values
      const headerHeight = `${this.header.offsetHeight}px`;
      if (document.documentElement.style.getPropertyValue('--header-height') === headerHeight) return;
      document.documentElement.style.setProperty('--header-height', headerHeight);
    });
  }

  onScroll() {
    if (!this.headerBounds.bottom || this.scrollFrame !== null) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.isConnected) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrolledPast = scrollTop > this.headerBounds.bottom;
      if (this.stickyType === 'on-scroll-up') {
        if (scrollTop > this.currentScrollTop && scrolledPast) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventHide) this.hide();
        } else if (scrollTop < this.currentScrollTop && scrolledPast) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            this.reveal();
          } else {
            clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
            this.hide();
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          this.reset();
        }
      } else {
        if (scrolledPast) {
          this.header.classList.add('scrolled-past-header');
          if (!this.sticky()) {
            this.hide();
            this.scheduleReveal();
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          this.reset();
        }
      }

      this.currentScrollTop = scrollTop;
    });
  }

  scheduleReveal() {
    if (this.revealTimer !== null) return;
    this.revealTimer = setTimeout(() => {
      this.revealTimer = null;
      if (this.isConnected) this.reveal();
    }, 100);
  }


  hide() {
    const isHidden =
      this.header.classList.contains('shopify-section-header-hidden') &&
      this.header.classList.contains('shopify-section-header-sticky') &&
      document.body.classList.contains('shopify-body-section-header-hidden') &&
      document.body.classList.contains('shopify-body-section-header-sticky');
    if (isHidden) return;

    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
    this.closeMenuItems();
    this.closeSearchModal();
    this.closeMenuDrawer();
  }

  reveal() {
    const isRevealed =
      this.header.classList.contains('shopify-section-header-sticky') &&
      this.header.classList.contains('animate') &&
      !this.header.classList.contains('shopify-section-header-hidden') &&
      document.body.classList.contains('shopify-body-section-header-sticky') &&
      !document.body.classList.contains('shopify-body-section-header-hidden');
    if (isRevealed) return;

    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
    document.body.classList.add('shopify-body-section-header-sticky');
    document.body.classList.remove('shopify-body-section-header-hidden');
  }

  reset() {
    clearTimeout(this.revealTimer);
    this.revealTimer = null;
    const isReset = !this.header.classList.contains('shopify-section-header-hidden') &&
      !this.header.classList.contains('shopify-section-header-sticky') &&
      !this.header.classList.contains('animate');
    if (isReset) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  sticky() {
    return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden') && document.body.classList.contains('shopify-body-section-header-sticky') && !document.body.classList.contains('shopify-body-section-header-hidden');
  }

  closeMenuDrawer() {
    this.menuDrawer = this.menuDrawer || this.header.querySelectorAll('menu-drawer');
    this.menuDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
  }

  closeMenuItems() {
    this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
    this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
  }

  closeSearchModal() {
    this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
    this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
  }
}

customElements.define('sticky-header', StickyHeader);

class ThemeSwiper extends HTMLElement {
  constructor() {
    super();
    this._onVideoEnded = null;
    this.lastOutIndex = null;
    this.swiper = null;
    this._controllerSwiper = null;
    this._controllerInitListener = null;
    this._syncFromControllerSlideChange = null;
    this._syncFromControllerClick = null;
    this.breakpointList = ['small', 'medium', 'large-up'];
    this.boundPlaySlider = this.playSlider.bind(this);
    this.boundPauseSlider = this.pauseSlider.bind(this);
  }

  get threshold() {
    return Number(this.getAttribute('data-threshold') ?? -200)
  }

  get container() {
    return this._container = this._container || this.querySelector('[data-swiper-container]') || this.querySelector('.swiper')
  }

  get wrapper() {
    return this._wrapper = this._wrapper || this.querySelector('[data-swiper-wrapper]') || this.querySelector('.swiper-wrapper')
  }

  get scrollbar() {
    return document.querySelector(`#Scrollbar-Slider-${this.dataset.sliderId}`) || this.querySelector(':scope > .swiper-scrollbar')
  }

  get pagination() {
    return document.querySelector(`#Pagination-Slider-${this.dataset.sliderId}`) || this.querySelector(':scope > .swiper-pagination')
  }

  get prev() {
    return document.querySelector(`#Navigation-Slider-${this.dataset.sliderId} .swiper-button-prev`) || this.querySelector(':scope > .swiper-button-prev')
  }
  
  get next() {
    return document.querySelector(`#Navigation-Slider-${this.dataset.sliderId} .swiper-button-next`) || this.querySelector(':scope > .swiper-button-next')
  }

  get playButton() {
    return this.querySelector('.swiper-button-play')
  }

  get pauseButton() {
    return this.querySelector('.swiper-button-pause')
  }

  get resolution() {
    return this.getAttribute('data-resolution')
  }

  get autoplayDelayTime() {
    return parseInt(this.getAttribute('data-autoplay-delay'))
  }

  getLogicalIndex(swiper) {
    return Number.isFinite(swiper?.realIndex) ? swiper.realIndex : swiper?.activeIndex;
  }

  get items() {
    if (!this._items) {
      let items = Array.from(this.querySelectorAll('[data-swiper-slide]'));
      if (items.length === 0) {
        items = Array.from(this.querySelectorAll('.swiper-slide'));
      }
      this._items = items;
    }
    return this._items
  }

  get markedItem() {
    return this._markedItem = this._markedItem || this.querySelector('.swiper-slide.swiper-slide-active [data-marked]');
  }

  connectedCallback() {
    if (this.dataset.desktopEffect) {
      this._desktopEffectQuery = window.matchMedia('(min-width: 1033px)');
      this._desktopEffectListener = () => {
        if (!this.swiper) return;
        const realIndex = this.swiper.realIndex;
        const thumbnails = this.swiper.thumbs?.swiper?.el?.closest('theme-swiper');
        const thumbnailIndex = thumbnails?.swiper?.realIndex;
        this.swiperDestroy();
        // Recreate the paired thumbs too: Swiper retains tap handlers on external thumbs.
        if (thumbnails && thumbnails !== this) {
          thumbnails.swiperDestroy();
          thumbnails.init();
          thumbnails.setActiveSlideItem(thumbnailIndex);
        }
        this.init();
        this.setActiveSlideItem(realIndex);
      };
      this._desktopEffectQuery.addEventListener('change', this._desktopEffectListener);
    }

    if (!this.getAttribute('loaded')) {
      createObserver({
        element: this,
        callback: this.init.bind(this),
        threshold: this.threshold
      });
    } else {
      this.init();
    }
    this.playButton?.addEventListener('click', this.boundPlaySlider);
    this.pauseButton?.addEventListener('click', this.boundPauseSlider);

    if (this.resolution) {
      const mq = window.matchMedia(this.resolution);
      this._resolutionListener = () => this.init();
      mq.addEventListener('change', this._resolutionListener);
    }
  }

  disconnectedCallback() {
    if (this._desktopEffectQuery && this._desktopEffectListener) {
      this._desktopEffectQuery.removeEventListener('change', this._desktopEffectListener);
      this._desktopEffectListener = null;
    }
    this.playButton?.removeEventListener('click', this.boundPlaySlider);
    this.pauseButton?.removeEventListener('click', this.boundPauseSlider);

    if (this.resolution && this._resolutionListener) {
      const mq = window.matchMedia(this.resolution);
      mq.removeEventListener('change', this._resolutionListener);
      this._resolutionListener = null;
    }
  }

  init() {
    this.options = this.swiperOptions();
    if(this.resolution != undefined) {
      if (window.matchMedia(this.resolution).matches) {
        if (!this.swiper) this.swiperInitilize();
      } else {
        if (this.swiper) this.swiperDestroy();
      }
    } else {
      if (!this.swiper) this.swiperInitilize();
    }
  }

  bindEvents() {
    if (!this.swiper) return;
  
    this._onSlideChange = () => {
      this.swiper.pagination.render();
      this.swiper.pagination.update();
      this.swiper.navigation.destroy();
      this.swiper.navigation.init();
      this.handleTabindex(this.swiper);
      this.dispatchEvent(new CustomEvent('slider:change', {
        bubbles: true,
        detail: {
          currentPage: this.getLogicalIndex(this.swiper),
          activeIndex: this.swiper.activeIndex
        }
      }));
    };
  
    this._onTransitionStart = () => {
      const oldIndex = this.swiper.previousIndex !== this.swiper.activeIndex
        ? this.swiper.previousIndex
        : this.lastOutIndex;
      const oldSlide = this.swiper.slides[oldIndex];
      if (oldSlide?.dataset.type === 'video') this.handleVideoOut(oldSlide, this.swiper);
      this.syncProgressDuration(this.swiper.slides[this.swiper.activeIndex]);
      this.swiper.mousewheel.enabled = false;
      this.lastOutIndex = this.swiper.previousIndex;
    };
  
    this._onTransitionEnd = () => {
      const newSlide = this.swiper.slides[this.swiper.activeIndex];
      if (newSlide?.dataset.type === 'video') this.handleVideoIn(newSlide, this.swiper);
      this.swiper.mousewheel.enabled = true;
      this.lastOutIndex = this.swiper.activeIndex;
      this.setSlideEdges(this.swiper);
      this.handleTabindex(this.swiper);
    };
  
    this._onPaginationUpdate = () => this.updatePagination(this.swiper);
    this._onSlideChangeTransitionEnd = () => this.handleLoop(this.swiper);
    this._onAutoplayPause = () => this.setAutoplayState(this.swiper);
    this._onAutoplayResume = () => this.setAutoplayState(this.swiper);
    this._onAutoplayStop = () => this.setAutoplayState(this.swiper);
    this._onAutoplayStart = () => this.setAutoplayState(this.swiper);
    this.swiper.on('slideChange', this._onSlideChange);
    this.swiper.on('transitionStart', this._onTransitionStart);
    this.swiper.on('transitionEnd', this._onTransitionEnd);
    this.swiper.on('paginationUpdate', this._onPaginationUpdate);
    this.swiper.on('slideChangeTransitionEnd', this._onSlideChangeTransitionEnd);
    this.swiper.on('autoplayPause', this._onAutoplayPause);
    this.swiper.on('autoplayResume', this._onAutoplayResume);
    this.swiper.on('autoplayStart', this._onAutoplayStart);
    this.swiper.on('autoplayStop', this._onAutoplayStop);
    this.setupControllerSync();

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      this._onBlockSelect = this.onBlockSelect.bind(this);
      this._onSectionSelect = this.onSectionSelect.bind(this);
      section.addEventListener('shopify:section:load', this._onSectionSelect);
      section.addEventListener('shopify:section:unload', this._onSectionSelect);
      section.addEventListener('shopify:section:reorder', this._onSectionSelect);
      this.addEventListener('shopify:block:select', this._onBlockSelect);
    }
  }
  
  unBindEvents() {
    if (!this.swiper) return;
  
    this.swiper.off('slideChange', this._onSlideChange);
    this.swiper.off('transitionStart', this._onTransitionStart);
    this.swiper.off('transitionEnd', this._onTransitionEnd);
    this.swiper.off('paginationUpdate', this._onPaginationUpdate);
    this.swiper.off('slideChangeTransitionEnd', this._onSlideChangeTransitionEnd);
    this.swiper.off('autoplayPause', this._onAutoplayPause);
    this.swiper.off('autoplayResume', this._onAutoplayResume);
    this.swiper.off('autoplayStart', this._onAutoplayStart);
    this.swiper.off('autoplayStop', this._onAutoplayStop);
    this.teardownControllerSync();
  
    this._onSlideChange = null;
    this._onTransitionStart = null;
    this._onTransitionEnd = null;
    this._onPaginationUpdate = null;
    this._onSlideChangeTransitionEnd = null;
    this._onAutoplayPause = null;
    this._onAutoplayResume = null;
    this._onAutoplayStop = null;
    this._onAutoplayStart = null;
    this.lastOutIndex = null;

    if (Shopify.designMode) {
      if(this._onBlockSelect) {
        this.removeEventListener('shopify:block:select', this._onBlockSelect);
        this._onBlockSelect = null;
      }

      if(this._onSectionSelect) {
        const section = this.closest('.shopify-section');
        section.removeEventListener('shopify:section:load', this._onSectionSelect);
        section.removeEventListener('shopify:section:unload', this._onSectionSelect);
        section.removeEventListener('shopify:section:reorder', this._onSectionSelect);
        this._onSectionSelect = null;
      }
    }
  }

  swiperOptions() {
    let defaultOptions = {};
    try {
      defaultOptions = JSON.parse(this.dataset.swiper || '{}');
    } catch (e) {
      console.error('Invalid JSON in data-swiper:', e);
    }
    let navigation = {
      prevEl: this.prev,
      nextEl: this.next
    };
    let pagination = {};
    if(this.pagination != undefined) {
      let isFraction = this.pagination.classList.contains('fraction');
      if (isFraction) {
        pagination = {
          el: this.pagination,
          type: 'fraction',
          renderFraction: (currentClass, totalClass) => `
            <span class="${currentClass}"></span>
            <span class="swiper-pagination-separator"> / </span>
            <span class="${totalClass}"></span>
          `
        };
      } else {
        pagination = {
          el: this.pagination,
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className}">${index + 1}</span>`;
          }
        };
      }
    }
    let options = { ...defaultOptions };
    // Effect changes require a fresh Swiper instance rather than a breakpoint override.
    if (this.dataset.desktopEffect && this._desktopEffectQuery?.matches) {
      options.effect = this.dataset.desktopEffect;
      options.fadeEffect = { ...options.fadeEffect, crossFade: true };
    }
    options = {
      ...options,
      pagination: {
        ...pagination
      },
      navigation: {
        ...navigation
      },
      scrollbar: {
        el: this.scrollbar,
        hide: false
      },
      a11y: {
        enabled: true
      },
      on: {
        init: (element) => {
          const selectedElement = element.slides[element.activeIndex];
          if (selectedElement && selectedElement.dataset.type === 'video') this.handleVideoIn(selectedElement, element);
          const indexInit = this.indexInit || this.dataset.activeIndexInit;
          indexInit && this.setActiveSlideItem(indexInit);
          this.setSlideHeight();
          this.setSlideEdges(element);
          this.checkNavigation(element);
          this.checkPagination(element);
          this.handleTabindex(element);
          this.updatePagination(element);
          element.slides.forEach((slide, index) => {
            const swiperSlideIndex = Number(slide.dataset.swiperSlideIndex);
            slide.setAttribute('data-index', Number.isFinite(swiperSlideIndex) ? swiperSlideIndex : index);
          });
          this.dispatchEvent(new CustomEvent('slider:change', {
            bubbles: true,
            detail: {
              currentPage: this.getLogicalIndex(element),
              activeIndex: element.activeIndex
            }
          }));
        },
        resize: (element) => {
          this.setSlideHeight();
          this.setSlideEdges(element);
          this.checkNavigation(element);
          this.checkPagination(element);
          this.handleTabindex(element);
        },
        update: (element) => {
          this.setSlideHeight();
          this.setSlideEdges(element);
          this.checkNavigation(element);
          this.checkPagination(element);
          this.handleTabindex(element);
        }
      }
    }

    return options
  }

  swiperInitilize(){
    if(!this.container.classList.contains('swiper')) this.container.classList.add('swiper');
    if(!this.wrapper.classList.contains('swiper-wrapper')) this.wrapper.classList.add('swiper-wrapper');
    this.querySelectorAll('[data-swiper-slide]').forEach((element) => {
      if(!element.classList.contains('swiper-slide')) element.classList.add('swiper-slide')
    });
    this.swiper = new Swiper(this.container, this.options);
    this.dispatchEvent(new CustomEvent('slider:init', {
      bubbles: true,
      detail: {
        id: this.dataset.sliderId,
        swiper: this.swiper
      }
    }));
    this.bindEvents();
  }

  swiperDestroy() {
    this.unBindEvents();
    this.restoreTabindex();
    this.swiper?.destroy(true, true);
    this.swiper = null;
    if(this.container.classList.contains('swiper')) this.container.classList.remove('swiper');
    if(this.wrapper.classList.contains('swiper-wrapper')) this.wrapper.classList.remove('swiper-wrapper');
    this.querySelectorAll('[data-swiper-slide]').forEach((element) => {
      if(element.classList.contains('swiper-slide')) element.classList.remove('swiper-slide')
    });
    this.dispatchEvent(new CustomEvent('slider:destroy', {
      bubbles: true,
      detail: {
        id: this.dataset.sliderId
      }
    }));
  }

  setActiveSlideItem(activeIndex) {
    if (typeof activeIndex === 'undefined' || activeIndex === null) return;
    const idx = Number(activeIndex);
    if (Number.isFinite(idx)) {
      this.swiper?.params?.loop ? this.swiper.slideToLoop(idx) : this.swiper?.slideTo(idx);
    }
  }

  setSlideHeight() {
    if (!this.markedItem) return;
    const slideHeight = `${this.markedItem.offsetHeight}px`;
    this.style.setProperty('--slide-height', slideHeight);
    this.closest('[data-slide-height-container]')?.style.setProperty('--slide-height', slideHeight);
  }

  setSlideEdges(swiper) {
    const isBeginning = swiper.isBeginning;
    const isEnd = swiper.isEnd;
    swiper.el.classList.toggle('show-left', !isBeginning);
    swiper.el.classList.toggle('show-right', !isEnd);
  }

  setAutoplayState(swiper) {
    if(!swiper?.autoplay) return;
    // Autoplay is stopped on purpose while a video slide plays; the progress bar should keep running then.
    const isStopped = swiper.autoplay.running === false && !this._videoHoldsAutoplay;
    const isPaused  = swiper.autoplay.paused === true && !isStopped;
    this.classList.toggle('theme-swiper--stopped', isStopped);
    this.classList.toggle('theme-swiper--paused', isPaused);
  }

  // Progress dots run on the autoplay delay, or on the video length once a video slide is playing.
  syncProgressDuration(slide) {
    if (!this.hasAttribute('data-autoplay-delay')) return;
    this.style.setProperty('--progress-duration', `${this.autoplayDelayTime}ms`);
    // Hold the bar at 0 on video slides until the video length is known.
    this.classList.toggle('theme-swiper--progress-wait', slide?.dataset.type === 'video');
  }

  syncProgressToVideo(videoElement) {
    this.cancelProgressSync();
    this._progressVideo = videoElement;
    this._onProgressPlay = async () => {
      const duration = await this.getVideoDuration(videoElement).catch(() => NaN);
      if (this._progressVideo !== videoElement) return;
      if (Number.isFinite(duration) && duration > 0) this.restartProgress(duration);
      else this.classList.remove('theme-swiper--progress-wait');
    };
    if (videoElement.playing) this._onProgressPlay();
    else videoElement.addEventListener('video:play', this._onProgressPlay, { once: true });
  }

  cancelProgressSync() {
    this._progressVideo?.removeEventListener('video:play', this._onProgressPlay);
    this._progressVideo = null;
    this._onProgressPlay = null;
  }

  restartProgress(duration) {
    this.style.setProperty('--progress-duration', `${duration * 1000}ms`);
    this.classList.add('theme-swiper--progress-restart');
    this.classList.remove('theme-swiper--progress-wait');
    void this.offsetWidth;
    this.classList.remove('theme-swiper--progress-restart');
  }

  // Remaining playback time in seconds, or NaN when the player can't tell.
  async getVideoDuration(videoElement) {
    const nativeVideo = videoElement.querySelector('video');
    if (nativeVideo) return this.getNativeVideoDuration(nativeVideo);
    const iframe = videoElement.querySelector('iframe');
    if (!iframe) return NaN;
    const host = videoElement.getAttribute('host');
    if (host === 'vimeo' && window.Vimeo?.Player) return new Vimeo.Player(iframe).getDuration();
    if (host === 'youtube' && window.YT?.get) {
      const ytPlayer = YT.get(iframe.id);
      if (ytPlayer?.getDuration) return ytPlayer.getDuration() - ytPlayer.getCurrentTime();
    }
    return NaN;
  }

  async getNativeVideoDuration(video) {
    if (!Number.isFinite(video.duration)) {
      await new Promise((resolve) => video.addEventListener('loadedmetadata', resolve, { once: true }));
    }
    // An ended video restarts from 0 on play(), but currentTime can still read as the end when `play` fires.
    const startedAt = video.ended || video.currentTime >= video.duration - 0.5 ? 0 : video.currentTime;
    return video.duration - startedAt;
  }

  releaseVideoAutoplay(swiper) {
    this._videoHoldsAutoplay = false;
    if (!swiper.autoplay.running) swiper.autoplay.start();
  }

  handleLoop(swiper) {
    if (this.hasAttribute('data-skip-loop-fix')) return;
    if (swiper && swiper.params.loop) swiper.loopFix();
  }

  handleVideoOut(oldElement, swiper) {
    if (!oldElement) return;
    const videoElement = window.visibleMedia(oldElement.querySelectorAll('video-media'));
    if (!videoElement) return;
    videoElement.pause();
    videoElement.updateControlButton(true);
    videoElement.updateSoundButton(true);
    this.cancelProgressSync();
    if (this._videoHoldsAutoplay) this.releaseVideoAutoplay(swiper);
    if (this._onVideoEnded) {
      videoElement.removeEventListener('video:ended', this._onVideoEnded);
      this._onVideoEnded = null;
    }
  }

  handleVideoIn(newElement, swiper) {
    if (!newElement) return;
    const videoElement = window.visibleMedia(newElement.querySelectorAll('video-media'));
    if (!videoElement) return;
    const autoplayEnabled = Boolean(swiper?.params?.autoplay?.enabled);
    const autoplayWasRunning = autoplayEnabled && swiper.autoplay.running;
    if (autoplayWasRunning) {
      // swiper.params.autoplay.delay = 0;
      this._videoHoldsAutoplay = true;
      swiper.autoplay.stop();
      this.syncProgressToVideo(videoElement);
    }
    videoElement.play();
    videoElement.updateControlButton(false);
    videoElement.updateSoundButton(false);
    const handleVideoEnd = () => {
      this._onVideoEnded = null;
      videoElement.removeEventListener('video:ended', handleVideoEnd);
      if (autoplayWasRunning) {
        this.releaseVideoAutoplay(swiper);
        // slideNext() does nothing on the last slide when loop is off, so rewind to the first slide explicitly.
        if (swiper.isEnd && !swiper.params.loop && !swiper.params.rewind) swiper.slideTo(0);
        else swiper.slideNext();
        //  swiper.params.autoplay.delay = this.autoplayDelayTime;
      }
    };
    this._onVideoEnded = handleVideoEnd;
    videoElement.addEventListener('video:ended', this._onVideoEnded);
  }

  handleTabindex(swiper) {
    if (!swiper) return;
    const focusableSelectors = 'a[href], button, input, textarea, select, summary, iframe, video[controls], audio[controls], [contenteditable="true"], [tabindex]';
    const originalTabindexAttribute = 'data-swiper-original-tabindex';
    const noTabindex = '__none__';
    let visibleSlides = swiper.slides.filter((slide) => slide.classList.contains('swiper-slide-visible'));

    if (visibleSlides.length === 0 && swiper.slides[swiper.activeIndex]) {
      visibleSlides = [swiper.slides[swiper.activeIndex]];
    }

    const visibleSlideSet = new Set(visibleSlides);
    swiper.slides.forEach((slide) => {
      const isVisible = visibleSlideSet.has(slide);
      slide.querySelectorAll(focusableSelectors).forEach((element) => {
        if (!element.hasAttribute(originalTabindexAttribute)) {
          element.setAttribute(originalTabindexAttribute, element.getAttribute('tabindex') ?? noTabindex);
        }

        if (!isVisible || element.hasAttribute('data-omit-tabindex')) {
          element.setAttribute('tabindex', '-1');
          return;
        }

        const originalTabindex = element.getAttribute(originalTabindexAttribute);
        if (originalTabindex === noTabindex) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', originalTabindex);
      });
    });
  }

  restoreTabindex() {
    const originalTabindexAttribute = 'data-swiper-original-tabindex';
    const noTabindex = '__none__';
    this.querySelectorAll(`[${originalTabindexAttribute}]`).forEach((element) => {
      const originalTabindex = element.getAttribute(originalTabindexAttribute);
      if (originalTabindex === noTabindex) element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', originalTabindex);
      element.removeAttribute(originalTabindexAttribute);
    });
  }

  handleKeyboard(event) {
    const keyCode = event.keyCode || event.which;
    const focusedElement = document.activeElement;
    if (this.swiper && this.swiper.el.contains(focusedElement)) {
      switch (keyCode) {
        case 37:
          this.swiper.slidePrev();
          break;
        case 39:
          this.swiper.slideNext();
          break;
      }
    }
  }

  playSlider(event) {
    this.togglePlayPause(event.currentTarget.name);
    if(this.swiper) {
      this.swiper.params.autoplay.delay = this.autoplayDelayTime;
      this.swiper.autoplay.start();
    }
    this.classList.remove('theme-swiper--paused');
  }

  pauseSlider(event) {
    this.togglePlayPause(event.currentTarget.name);
    if(this.swiper) {
      this.swiper.params.autoplay.delay = 0;
      this.swiper.autoplay.stop();
    }
    this.classList.add('theme-swiper--paused');
  }

  togglePlayPause(state = 'play') {
    if(state == 'play') {
      this.playButton?.classList.add('hidden');
      this.pauseButton?.classList.remove('hidden');
    } else if(state == 'pause') {
      this.pauseButton?.classList.add('hidden');
      this.playButton?.classList.remove('hidden');
    }
  }

  updatePagination(swiper) {
    if (!swiper) return;
    if (swiper.params?.pagination?.type !== 'bullets') return;
    const cloneSlides = swiper.el.querySelectorAll('.swiper-slide-duplicate, .swiper-slide--cloned');
    if (cloneSlides.length === 0) return;
    const bullets = swiper.pagination.bullets;
    if (bullets.length <= 1) return;
    const index = swiper.realIndex - 1;
    const halfLength = Math.floor(bullets.length / 2);
    if (index >= halfLength - 1) {
      for (let i = 0; i < halfLength; i++){
        bullets[i].style.display = 'none';
      }
    } else {
      for (let i = halfLength; i < bullets.length; i++) {
        bullets[i].style.display = 'none';
      }
    }
  }

  checkPagination(swiper) {
    let pagination = swiper.pagination;
    if (!pagination.el || pagination.bullets.length == 1) return;
    let breakpoint = [...this.breakpointList].find((breakpoint) => window.matchMedia(convertBreakpoint(breakpoint)).matches);
    this.classList.toggle('has-pagination', !pagination.el.classList.contains(`${breakpoint}-hide`));
  }

  checkNavigation(swiper) {
    let navigation = swiper.navigation;
    if (!navigation?.nextEl && !navigation?.prevEl) return;
    const nextDisabled = navigation.nextEl?.hasAttribute('disabled');
    const prevDisabled = navigation.prevEl?.hasAttribute('disabled');
    if (nextDisabled && prevDisabled) return;
    let breakpoint = [...this.breakpointList].find((breakpoint) => window.matchMedia(convertBreakpoint(breakpoint)).matches);
    this.classList.toggle('has-navigation', !navigation.nextEl.closest('.swiper-navigation').classList.contains(`${breakpoint}-hide`));
  }

  onSectionSelect(event) {
    const activeItem = event.target.querySelector('.swiper-slide-active');
    if(!activeItem) return;
    // if(!this.indexInit) this.indexInit = Number(activeItem.dataset.index);
    this.setActiveSlideItem(activeItem.dataset.index)
  }

  onBlockSelect(event) {
    // if(!this.indexInit) this.indexInit =  Number(event.target.dataset.index);
    this.setActiveSlideItem(event.target.dataset.index)
  }

  resolvePeerSwiper(selector) {
    if (!selector) return null;
    try {
      const element = document.querySelector(selector);
      return element?.swiper ?? null;
    } catch (error) {
      return null;
    }
  }

  setupControllerSync() {
    this.teardownControllerSync();
    if (!this.swiper) return;
    const controllerSelector = this.dataset.controlledBy;
    if (!controllerSelector) return;

    const attach = (controllerSwiper) => {
      if (!controllerSwiper) return;
      const controllerEl = controllerSwiper.el;
      const thisId = this.id || this.dataset.sliderId;
      const configuredThumbs = this.swiper?.thumbs?.swiper || this.swiper?.params?.thumbs?.swiper;
      if (configuredThumbs) {
        const isConfiguredInstance = configuredThumbs === controllerSwiper;
        const isConfiguredElement = configuredThumbs === controllerEl;
        const isConfiguredSelector = typeof configuredThumbs === 'string' ? document.querySelector(configuredThumbs)?.swiper === controllerSwiper : false;
        if (!isConfiguredInstance && !isConfiguredElement && !isConfiguredSelector) return;
      } else {
        const thumbsTarget = controllerEl?.closest('theme-swiper')?.dataset?.thumbsFor;
        if (!thumbsTarget || (thumbsTarget !== thisId && thumbsTarget !== `#${thisId}`)) return;
      }

      this._controllerSwiper = controllerSwiper;
      // Avoid "last visible thumb click jumps to next group" behavior.
      if (this._controllerSwiper?.params?.slideToClickedSlide) {
        this._controllerSwiper.params.slideToClickedSlide = false;
      }

      const getIndexFromSlideEl = (slideEl) => {
        if (!slideEl) return NaN;
        const v = slideEl.dataset?.index;
        const n = Number(v);
        return Number.isFinite(n) ? n : NaN;
      };

      const slideMainTo = (index) => {
        if (!Number.isFinite(index)) return;
        if (this.swiper?.params?.loop) this.swiper.slideToLoop(index);
        else this.swiper.slideTo(index);
      };

      this._syncFromControllerClick = (swiper) => {
        const clickedIndex = getIndexFromSlideEl(swiper.clickedSlide);
        if (Number.isFinite(clickedIndex)) {
          slideMainTo(clickedIndex);
          swiper.updateSlidesClasses();
          return;
        }
      };

      this._syncFromControllerSlideChange = () => {
        const idx = Number.isFinite(controllerSwiper.realIndex) ? controllerSwiper.realIndex : controllerSwiper.activeIndex;
        slideMainTo(idx);
      };

      controllerSwiper.on('click', this._syncFromControllerClick);
      controllerSwiper.on('slideChange', this._syncFromControllerSlideChange);

      this._syncFromControllerSlideChange();
    };

    const controllerSwiper = this.resolvePeerSwiper(controllerSelector);

    if (controllerSwiper) {
      attach(controllerSwiper);
    } else {
      this._controllerInitListener = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (!target.matches(controllerSelector)) return;

        attach(target.swiper);

        if (this._controllerInitListener) {
          document.removeEventListener('slider:init', this._controllerInitListener);
          this._controllerInitListener = null;
        }
      };
      document.addEventListener('slider:init', this._controllerInitListener);
    }
  }

  teardownControllerSync() {
    if (this._controllerSwiper) {
      if (this._syncFromControllerSlideChange) this._controllerSwiper.off('slideChange', this._syncFromControllerSlideChange);
      if (this._syncFromControllerClick) this._controllerSwiper.off('click', this._syncFromControllerClick);
    }
    if (this._controllerInitListener) {
      document.removeEventListener('slider:init', this._controllerInitListener);
    }
    this._controllerSwiper = null;
    this._syncFromControllerSlideChange = null;
    this._syncFromControllerClick = null;
    this._controllerInitListener = null;
  }
}

customElements.define('theme-swiper', ThemeSwiper);

class ThemeSwiperControls extends HTMLElement {
  constructor() {
    super();
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.paginationEl = this.querySelector('.theme-swiper-controls__pagination');
    this._onPrevClick = this.handlePrevClick.bind(this);
    this._onNextClick = this.handleNextClick.bind(this);
    this._onSliderInit = (event) => { 
      if (this.matchesSlider(event)) {
        this.startMirroring();
        this.update();
      }
    };
    this._onSliderChange = (event) => {
      if (this.matchesSlider(event)) {
        this.syncCloneClasses();
        this.update();
      }
    };
    this._onSliderDestroy= (event) => {
      if (this.matchesSlider(event)) {
        this.stopMirroring();
        this.update();
      }
    };
    this._onCloneClick = (event) => {
      const bullet = event.target.closest('.swiper-pagination-bullet');
      if (!bullet || !this.paginationEl?.contains(bullet)) return;
      if (this.paginationType !== 'bullets') return;
      const idx = Array.from(this.paginationEl.querySelectorAll('.swiper-pagination-bullet')).indexOf(bullet);
      const origBullets = this.originalBullets;
      if (idx > -1 && origBullets[idx]) {
        origBullets[idx].click();
      }
    };

    this._observer = null;
  }

  get sliderId() {
    return this._sliderId = this._sliderId || this.dataset.id
  }

  get sliderElement() {
    if (this._sliderElement && document.body.contains(this._sliderElement)) return this._sliderElement;
    this._sliderElement = document.querySelector(`theme-swiper[data-slider-id="${this.sliderId}"]`) || document.querySelector(`theme-swiper[data-id="${this.sliderId}"]`) || null;
    return this._sliderElement
  }

  get swiper() {
    return this.sliderElement?.swiper ?? null
  }

  get originalPaginationEl() {
    return this.swiper?.pagination?.el || this.sliderElement?.querySelector('.swiper-pagination') || null
  }

  get paginationType() {
    const t = this.swiper?.params?.pagination?.type;
    if (t) return t;
    const el = this.originalPaginationEl;
    if (!el) return 'bullets';
    if (el.classList.contains('swiper-pagination-fraction')) return 'fraction';
    if (el.classList.contains('swiper-pagination-progressbar')) return 'progressbar';
    return 'bullets';
  }

  get originalBullets() {
    const root = this.originalPaginationEl;
    return root ? Array.from(root.querySelectorAll('.swiper-pagination-bullet')) : [];
  }

  connectedCallback() {
    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);
    this.paginationEl?.addEventListener('click', this._onCloneClick);
    document.addEventListener('slider:init', this._onSliderInit);
    document.addEventListener('slider:change', this._onSliderChange);
    document.addEventListener('slider:destroy', this._onSliderDestroy);
    if (this.swiper) this.startMirroring();
    this.update();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    this.paginationEl?.removeEventListener('click', this._onCloneClick);
    document.removeEventListener('slider:init', this._onSliderInit);
    document.removeEventListener('slider:change', this._onSliderChange);
    document.removeEventListener('slider:destroy', this._onSliderDestroy);
    this.stopMirroring();
  }

  matchesSlider(event) {
    const idFromDetail = event?.detail?.id;
    const idFromTarget = event?.target?.dataset?.sliderId || event?.target?.dataset?.id;
    return (idFromDetail && idFromDetail == this.sliderId) || (idFromTarget && idFromTarget == this.sliderId);
  }

  handlePrevClick(event) {
    event.preventDefault();
    this.handleButtonsClick('previous');
  }

  handleNextClick(event) {
    event.preventDefault();
    this.handleButtonsClick('next');
  }

  handleButtonsClick(name = 'previous') {
    const swiper = this.sliderElement?.swiper;
    if (swiper) {
      (name == 'previous') ? swiper.slidePrev() : swiper.slideNext()
    } else {
      (name == 'previous') ? this.sliderElement?.prev?.click() : this.sliderElement?.next?.click()
    }
    this.update();
  }

  update() {
    const swiper = this.sliderElement?.swiper;
    
    if (!swiper) {
      this.prevButton?.setAttribute('disabled', 'true');
      this.nextButton?.setAttribute('disabled', 'true');
      this.prevButton?.setAttribute('aria-disabled', 'true');
      this.nextButton?.setAttribute('aria-disabled', 'true');
      return;
    }

    if (swiper.params.loop) {
      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
      this.prevButton?.setAttribute('aria-disabled', 'false');
      this.nextButton?.setAttribute('aria-disabled', 'false');
    } else {
      if (swiper.isBeginning) {
        this.prevButton?.setAttribute('disabled', 'true');
        this.prevButton?.setAttribute('aria-disabled', 'true');
      } else {
        this.prevButton?.removeAttribute('disabled');
        this.prevButton?.setAttribute('aria-disabled', 'false');
      }

      if (swiper.isEnd) {
        this.nextButton?.toggleAttribute('disabled', 'true');
        this.nextButton?.setAttribute('aria-disabled', 'true');
      } else {
        this.nextButton?.removeAttribute('disabled');
        this.nextButton?.setAttribute('aria-disabled', 'false');
      }
    }
  }

  startMirroring() {
    const swiper = this.swiper;
    const orig = this.originalPaginationEl;
    if (!swiper || !orig || !this.paginationEl) return;
    try { swiper.pagination?.render?.(); swiper.pagination?.update?.(); } catch {}
    this.rebuildCloneFromOriginal();
    if (!this._observer) {
      this._observer = new MutationObserver((mutations) => {
        const needsRebuild = mutations.some(m => m.type === 'childList');
        if (needsRebuild) {
          this.rebuildCloneFromOriginal();
        } else {
          this.syncCloneClasses();
        }
      });
      this._observer.observe(orig, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class','aria-current','aria-selected','aria-label']
      });
    }
  }

  stopMirroring() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  rebuildCloneFromOriginal() {
    const orig = this.originalPaginationEl;
    if (!orig || !this.paginationEl) return;
    this.paginationEl.className = `theme-swiper-controls__pagination ${orig.className}`.trim();
    this.paginationEl.innerHTML = orig.innerHTML;

    if (this.paginationType === 'bullets') {
      this.paginationEl.querySelectorAll('.swiper-pagination-bullet').forEach((bullet, index) => {
        bullet.dataset.index = String(index);
        bullet.setAttribute('type', 'button');
      });
    } else if (this.paginationType === 'fraction') {
      this.paginationEl.setAttribute('aria-live', 'polite');
      this.paginationEl.setAttribute('aria-atomic', 'true');
    }
    this.syncCloneClasses();
  }

  syncCloneClasses() {
    const type = this.paginationType;
    const orig = this.originalPaginationEl;
    if (!orig || !this.paginationEl) return;

    if (type === 'bullets') {
      const origBullets  = this.originalBullets;
      const cloneBullets = Array.from(this.paginationEl.querySelectorAll('.swiper-pagination-bullet'));
      const len = Math.min(origBullets.length, cloneBullets.length);
      for (let i = 0; i < len; i++) {
        const idx = cloneBullets[i].dataset.index;
        cloneBullets[i].className = origBullets[i].className;
        const ac = origBullets[i].getAttribute('aria-current');
        const as = origBullets[i].getAttribute('aria-selected');
        if (ac !== null) cloneBullets[i].setAttribute('aria-current', ac); else cloneBullets[i].removeAttribute('aria-current');
        if (as !== null) cloneBullets[i].setAttribute('aria-selected', as); else cloneBullets[i].removeAttribute('aria-selected');
        cloneBullets[i].dataset.index = idx ?? String(i);
      }
    } else if (type === 'fraction') {
      const oCur = orig.querySelector('.swiper-pagination-current');
      const oTot = orig.querySelector('.swiper-pagination-total');
      const cCur = this.paginationEl.querySelector('.swiper-pagination-current');
      const cTot = this.paginationEl.querySelector('.swiper-pagination-total');
      if (oCur && cCur) {
        cCur.textContent = oCur.textContent;
        cCur.className = oCur.className;
      }
      if (oTot && cTot) {
        cTot.textContent = oTot.textContent;
        cTot.className = oTot.className;
      }
    } else {
      this.paginationEl.innerHTML = orig.innerHTML;
    }
  }
}

customElements.define('theme-swiper-controls', ThemeSwiperControls);

class TabComponent extends HTMLElement {
  constructor() {
    super();
    this._onSliderChange = (event) => this.matchesSlider(event) && this.update();
  }

  get allItems() {
    return this._allItems = this._allItems || this.querySelectorAll('[data-filter]')
  }
  
  get sliderId() {
    return this._sliderId = this._sliderId || this.dataset.sliderId
  }

  get sliderElement() {
    if (this._sliderElement && document.body.contains(this._sliderElement)) return this._sliderElement;
    this._sliderElement = document.querySelector(`theme-swiper[data-slider-id="${this.sliderId}"]`) || document.querySelector(`theme-swiper[data-id="${this.sliderId}"]`) || null;

    return this._sliderElement;
  }

  get allContents() {
    return this._allContents = this._allContents || this.querySelectorAll('[data-tab-id]')
  }

  connectedCallback() {
    document.addEventListener('slider:change', this._onSliderChange);
    if (Shopify && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const target = event.target;
        let targetData, button;
        if (target.classList.contains('swiper-slide')) {
          const tabElement = target.closest('[data-tab-id]');
          if (!tabElement) return;
          targetData = JSON.parse(tabElement.dataset.shopifyEditorBlock);
          button = this.querySelector(`[data-tab="#Tab-${targetData.domId}"]`);
          if (button) this.open(button, `Tab-${targetData.domId}`);
          const swiper = target.closest('theme-swiper');
          if (swiper) {
            swiper.addEventListener('slider:init', () => {
              swiper.setActiveSlideItem(target.dataset.index);
            }, { once: true })
          }
        } else {
          targetData = JSON.parse(target.dataset.shopifyEditorBlock);
          button = this.querySelector(`[data-tab="#Tab-${targetData.domId}"]`);
          if (button) this.open(button, `Tab-${targetData.domId}`);
        }
        [...this.allContents].forEach((content) => {
          this.load(content);
        });
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('slider:change', this._onSliderChange);
  }

  matchesSlider(event) {
    const idFromDetail = event?.detail?.id;
    const idFromTarget = event?.target?.dataset?.sliderId || event?.target?.dataset?.id;
    return (idFromDetail && idFromDetail == this.sliderId) || (idFromTarget && idFromTarget == this.sliderId);
  }

  filtering(value) {
    if (value == 'all') {
      Array.from(this.allItems).forEach((item) => item.classList.remove('hidden'));
    } else {
      const items = this.querySelectorAll(`[data-filter=${value}]`);
      Array.from(this.allItems).forEach((item) => item.classList.add('hidden'));
      Array.from(items).forEach((item, index) => {
        item.classList.remove('hidden')
        if (index == 0) item.querySelector('button')?.click();
      });
    }
  }

  open(button, tab) {
    if (button) this.setActiveElement(button);
    this.activateTab(tab || button?.dataset.tab);
  }

  activateTab(tab) {
    const tabId = String(tab || '').trim().replace(/^#/, '');
    if (!tabId) return;

    const targetTabContent = Array.from(this.allContents).find((content) => content.dataset.tabId === tabId);
    if (!targetTabContent) return;

    this.load(targetTabContent);

    const openers = this.getTabOpeners();
    const isActiveOpener = (opener) => String(opener.dataset.tab || '').trim().replace(/^#/, '') === tabId;

    openers.forEach((opener) => {
      opener.classList.toggle('active', isActiveOpener(opener));
    });

    const tabItems = new Set(openers.map((opener) => opener.closest('li')).filter(Boolean));
    tabItems.forEach((item) => {
      const isActive = openers.some((opener) => opener.closest('li') === item && isActiveOpener(opener));
      item.classList.toggle('active', isActive);
    });

    this.querySelectorAll("[data-banner-tab-id]").forEach((media) => {
      media.classList.toggle("active", media.dataset.bannerTabId === tabId);
    });

    Array.from(this.allContents).forEach((content) => {
      content.classList.toggle('active', content === targetTabContent);
    });

    requestAnimationFrame(() => {
      targetTabContent.querySelectorAll('theme-swiper').forEach((slider) => {
        slider.init?.();
        slider.swiper?.update();
        slider.setSlideHeight?.();
      });
    });
  }

  getTabOpeners() {
    const roots = [this];

    this.querySelectorAll('[data-side-drawer]').forEach((drawerOpener) => {
      const drawerSelector = drawerOpener.getAttribute('data-side-drawer');
      if (!drawerSelector) return;

      try {
        const drawer = document.querySelector(drawerSelector);
        if (drawer) roots.push(drawer);
      } catch (error) {
        console.warn(`Invalid side drawer selector: ${drawerSelector}`, error);
      }
    });

    return Array.from(new Set(
      roots.flatMap((root) => Array.from(root.querySelectorAll('[data-tab]')))
    ));
  }

  load(tab) {
    if (!tab.getAttribute('loaded')) {
      const template = tab.querySelector(':scope template');
      if (!(template instanceof HTMLTemplateElement)) return;
      template.replaceWith(template.content.cloneNode(true));
      tab.setAttribute('loaded', true);
    }
  }

  update() {
    const swiper = this.sliderElement?.swiper;
    if (!swiper) return;
    const selectedElement = swiper.slides[swiper.activeIndex];
    const button = selectedElement?.querySelector('button[data-tab]');
    if (!button) return;
    this.setActiveElement(button);
    this.activateTab(button.dataset.tab);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('tab-component', TabComponent);

class TemplateComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    createObserver({
      element: this,
      callback: this.init.bind(this),
      threshold: 0
    });
  }

  init() {
    const template = this.querySelector('.template');
    if (!template) return;
    if (!this.getAttribute('loaded')) {
      template.replaceWith(template.content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
    }
  }
}

customElements.define('template-component', TemplateComponent);

class TextareaElement extends HTMLElement {
  constructor() {
    super();
    this.textarea = this.querySelector('textarea[maxlength]');
    this.counter = this.querySelector(':scope > span');
    this.updateRemainingCharacters = this.updateRemainingCharacters.bind(this);
  }

  connectedCallback() {
    if (!this.textarea || !this.counter) return;
    this.maxLength = Number(this.textarea.getAttribute('maxlength'));
    if (Number.isNaN(this.maxLength)) return;
    this.counter.setAttribute('aria-live', 'polite');
    this.textarea.addEventListener('input', this.updateRemainingCharacters);
    this.updateRemainingCharacters();
  }

  disconnectedCallback() {
    if (this.textarea) this.textarea.removeEventListener('input', this.updateRemainingCharacters);
  }

  updateRemainingCharacters() {
    const length = this.textarea.value.length;
    if (this.dataset.counter === 'count') {
      this.counter.textContent = `${length}/${this.maxLength}`;
      return;
    }
    const remainingCharacters = Math.max(this.maxLength - length, 0);
    this.counter.textContent = `${remainingCharacters} characters remaining`;
  }
}

customElements.define('textarea-element', TextareaElement);

class TrackingViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.expiresDate = parseInt(this.dataset.expiresDate) || 30;
    this.delay = parseInt(this.dataset.delaySeconds) || 0;
  }

  connectedCallback() {
    this.recordTimeout = setTimeout(() => {
      this.recordList({
        name: 'shopify_recently_viewed_products',
        expire: this.expiresDate,
        max: 10
      });
    }, this.delay * 1000);
  }

  disconnectedCallback() {
    if (this.recordTimeout) clearTimeout(this.recordTimeout);
  }

  setList(params, list) {
    if (this.dataset.productId !== '') {
      let productId = this.dataset.productId,
        position = list.indexOf(productId);
      if (position === -1) {
        list.unshift(productId);
        list = list.splice(0, params.max);
      } else {
        list.splice(position, 1);
        list.unshift(productId);
      }
      setCookie(params.name, list.join(' '), params.expire);
    }
  }

  recordList(params) {    
    this.setList(params, readCookie(params.name));
  }
}

customElements.define('tracking-viewed-products', TrackingViewedProducts);

class VideoMedia extends HTMLElement {
  constructor() {
    super();
    this.onPosterClick = this.onPosterClick.bind(this);
    this.togglePlayPause = this.togglePlayPause.bind(this);
    this.toggleSound = this.toggleSound.bind(this);
  }

  connectedCallback() {
    if (this.botDetected) {
      this.setAttribute('data-video-bot-skipped', 'true');
      this.classList.remove('loading');
    }

    if(!this.lazyLoad && !this.botDetected) {
      createObserver({
        element: this,
        callback: this.init.bind(this),
        threshold: 0
      });
    }

    this.posterElement?.addEventListener('click', this.onPosterClick);
    this.controlElement?.addEventListener('click', this.togglePlayPause);
    this.soundElement?.addEventListener('click', this.toggleSound);
  }

  disconnectedCallback() {
    this.posterElement?.removeEventListener('click', this.onPosterClick);
    this.controlElement?.removeEventListener('click', this.togglePlayPause);
    this.soundElement?.removeEventListener('click', this.toggleSound);
  }

  static get observedAttributes() {
    return ['playing', 'muted'];
  }

  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  get botDetected() {
    return typeof isBot === 'function' && isBot();
  }

  get playing() {
    return this.hasAttribute('playing')
  }

  get muted() {
    return this.hasAttribute('muted')
  }

  get posterElement() {
    return this.querySelector('[id^="Deferred-Poster-"]')
  }

  get controlElement() {
    return this.querySelector('.media__control-button')
  }

  get soundElement() {
    return this.querySelector('.media__sound-button')
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  init() {
    if (this.botDetected) return;
    if (this.autoplay) if (!this.paused) this.play();
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play({ userInitiated: true });
    }
    this.updateControlButton(this.paused);
  }

  togglePlayPause(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play({ userInitiated: true });
    }
    this.updateControlButton(this.paused);
  }

  toggleSound(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.muted) {
      this.unmuted = true;
      this.soundOn();
    } else {
      this.unmuted = false;
      this.soundOff();
    }
    this.updateSoundButton(this.unmuted);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
        this.paused = false;
        this.updateControlButton(this.paused);
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
        this.paused = true;
        this.updateControlButton(this.paused);
      }
    } else if (name === 'muted') {
      this.updateSoundButton(newValue === null);
    }
  }

  play(options = {}) {
    if (this.botDetected && !options.userInitiated) {
      this.setAttribute('data-video-bot-skipped', 'true');
      this.classList.remove('loading');
      return;
    }
    if (!this.playing) this.player.play();
  }

  pause() {
    if (this.playing) this.player.pause();
  }

  soundOff() {
    if (!this.muted) this.player.muted();
  }

  soundOn() {
    if (this.muted) this.player.unmuted();
  }

  updateControlButton(paused = false) {
    this.controlElement?.classList.toggle('is-pause', paused);
    this.controlElement?.classList.toggle('is-play', !paused);
  }

  updateSoundButton(unmuted = false) {
    this.soundElement?.classList.toggle('is-unmuted', unmuted);
    this.soundElement?.classList.toggle('is-muted', !unmuted);
  }

  hydrateMediaTemplate() {
    const templateElement = this.querySelector('template');
    if (templateElement) {
      const mediaElement = templateElement.content?.firstElementChild;
      if (mediaElement) templateElement.replaceWith(mediaElement.cloneNode(true));
      return;
    }

    const scriptTemplate = this.querySelector('script[type="text/template"][data-video-template]');
    if (!scriptTemplate) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = (scriptTemplate.textContent || '').trim();
    const mediaElement = wrapper.firstElementChild;
    if (mediaElement) scriptTemplate.replaceWith(mediaElement);
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '');
      this.classList.remove('loading');
      return new Promise(async (resolve) => {
        this.hydrateMediaTemplate();
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1032px)').matches;
        const script = document.createElement('script');
        script.type = 'text/javascript';

        if (this.getAttribute('host') === 'youtube') {
          const iframeElement = this.querySelector('iframe');
          if (!iframeElement) {
            resolve(null);
            return;
          }

          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => script.onload = resolve2);
          }

          await onYouTubePromise;

          const player = new YT.Player(iframeElement, {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                  this.setAttribute('muted', '');
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '');
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing');
                  if (event.data === YT.PlayerState.ENDED) {
                    this.dispatchEvent(new CustomEvent('video:ended', { bubbles: true }));
                  }
                }

                if (player.isMuted()) {
                  this.setAttribute('muted', '');
                } else {
                  this.removeAttribute('muted');
                }
              }
            }
          });
        }

        if (this.getAttribute('host') === 'vimeo') {
          const iframeElement = this.querySelector('iframe');
          if (!iframeElement) {
            resolve(null);
            return;
          }

          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => script.onload = resolve2);
          }

          const player = new Vimeo.Player(iframeElement);

          if (muteVideo) {
            player.setMuted(true);
            this.setAttribute('muted', '');
          }

          player.on('play', () => this.setAttribute('playing', ''));
          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => {
            this.removeAttribute('playing');
            this.dispatchEvent(new CustomEvent('video:ended', { bubbles: true }));
          });
          player.on('volumechange', () => {
            if (player.muted) {
              this.setAttribute('muted', '');
            } else {
              this.removeAttribute('muted');
            }
          });

          resolve(player);
        }
      });
    } else {
      this.hydrateMediaTemplate();
      this.setAttribute('loaded', '');
      this.classList.remove('loading');
      const player = this.querySelector('video');
      if (!player) return null;
      if (player.muted) this.setAttribute('muted', '');
      player.setAttribute('playsinline', '');
      player.setAttribute('preload', 'auto');
      player.load();

      if (player.muted && player.autoplay) {
        const tryPlay = player.play();
        if (tryPlay && typeof tryPlay.catch === 'function') {
          tryPlay.catch(() => {
            this.setAttribute('suspended', '');
            // player.controls = true;
          });
        }
      }

      player.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
      });

      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing');
        }
      });

      player.addEventListener('volumechange', () => {
        if (player.muted) {
          this.setAttribute('muted', '');
        } else {
          this.removeAttribute('muted');
        }
      });

      const handleEnd = () => {
        this.dispatchEvent(new CustomEvent('video:ended', { bubbles: true }));
      };

      const setupLoopDetection = () => {
        // Looping videos never fire `ended`, so report each pass near the end and re-arm once the video restarts.
        let reported = false;
        const onTimeUpdate = () => {
          const duration = player.duration;
          if (isNaN(duration)) return;
          const nearEnd = player.currentTime >= duration - 0.3;
          if (nearEnd && !reported) {
            reported = true;
            handleEnd();
          } else if (!nearEnd) {
            reported = false;
          }
        };
        player.addEventListener('timeupdate', onTimeUpdate);
      };

      if (player.loop) {
        if (isNaN(player.duration)) {
          player.addEventListener('loadedmetadata', setupLoopDetection, { once: true });
        } else {
          setupLoopDetection();
        }
      } else {
        player.addEventListener('ended', handleEnd);
      }

      return player;
    }
  }

  playerHandler(target, prop) {
    if (!target) return;

    if (this.getAttribute('host') === 'youtube') {
      if (prop === 'play') {
        target.playVideo();
      } else if (prop === 'pause') {
        target.pauseVideo();
      } else if (prop === 'muted') {
        target.mute();
        this.setAttribute('muted', '');
      } else if (prop === 'unmuted') {
        target.unMute();
        this.removeAttribute('muted');
      }
    } else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            target.controls = true;
          }
        });
      } else if (prop === 'muted' && !this.hasAttribute('host')) {
        target.muted = true;
      } else if (prop === 'unmuted' && !this.hasAttribute('host')) {
        target.muted = false;
      } else {
        target[prop]();
      }
    }
  }
}

customElements.define('video-media', VideoMedia);

class VideoMediaControls extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.onButtonClick.bind(this));
  }

  get targetId() {
    return this._targetId = this._targetId || this.dataset.id
  }

  get playing() {
    return this.hasAttribute('playing');
  }
  
  set playing(value) {
    if (value) {
      this.setAttribute('playing', '');
    } else {
      this.removeAttribute('playing');
    }
  }

  get targetElement() {
    if (this._targetElement && document.body.contains(this._targetElement)) return this._targetElement;
    this._targetElement = document.getElementById(this.targetId);
    return this._targetElement;
  }

  connectedCallback() {
    // code here
  }

  disconnectedCallback() {
    // code here
  }

  onButtonClick() {
    const target = this.targetElement || document.querySelector(`[id=${this.targetId}]`);
    if (!target) return;

    const videoElements = target.querySelectorAll('video-media');
    if (!videoElements) return;

    Array.from(videoElements).forEach((videoElement) => {
      if (videoElement.playing) {
        videoElement.pause();
      } else {
        videoElement.play();
      }
    });

    this.playing = !this.playing;
    this.updateControlButton(this.playing);
  }

  updateControlButton(paused = false) {
    this.button?.classList.toggle('is-pause', !paused);
    this.button?.classList.toggle('is-play', paused);
  }
}
customElements.define('video-media-controls', VideoMediaControls);

class SearchToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector(':scope > button');
    this.wrapper = this.querySelector(':scope > .header__search-full');
    this.input = this.wrapper ? this.wrapper.querySelector('input[type="search"]') : null;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onBodyClick = this.onBodyClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onWrapperTransitionEnd = this.onWrapperTransitionEnd.bind(this);
  }

  connectedCallback() {
    if (!this.button) return;
    this.updateWrapperAccessibility(this.isOpen());
    this.button.addEventListener('click', this.onButtonClick);
    this.addEventListener('keyup', this.onKeyUp);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onButtonClick);
    this.removeEventListener('keyup', this.onKeyUp);
    document.body.removeEventListener('click', this.onBodyClick);
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  updateWrapperAccessibility(isOpen) {
    if (!this.wrapper) return;
    this.wrapper.toggleAttribute('inert', !isOpen);
    if (isOpen) {
      this.wrapper.removeAttribute('aria-hidden');
    } else {
      this.wrapper.setAttribute('aria-hidden', 'true');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyUp(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
  }

  open() {
    this.setAttribute('open', '');
    this.button.setAttribute('aria-expanded', 'true');
    this.updateWrapperAccessibility(true);
    document.body.addEventListener('click', this.onBodyClick);
    if (this.wrapper) {
      this.wrapper.addEventListener('transitionend', this.onWrapperTransitionEnd);
    }
    if (this.input) {
      window.requestAnimationFrame(() => this.input.focus());
    }
  }

  close() {
    this.removeAttribute('open');
    this.button.setAttribute('aria-expanded', 'false');
    document.body.removeEventListener('click', this.onBodyClick);
    if (this.input) this.input.blur();
    this.updateWrapperAccessibility(false);
    if (this.wrapper) this.wrapper.classList.remove('header__search-full--expanded');
  }

  onWrapperTransitionEnd(event) {
    if (event.target !== this.wrapper || event.propertyName !== 'width') return;
    this.wrapper.removeEventListener('transitionend', this.onWrapperTransitionEnd);
    if (this.isOpen()) this.wrapper.classList.add('header__search-full--expanded');
  }
}

customElements.define('search-toggle', SearchToggle);


(() => {
  if (window.__themeRuntimeInitialized) return;
  window.__themeRuntimeInitialized = true;
  window.__themeLoadedAssets ||= new Set();
  const ASSET_SELECTOR = '[data-theme-loader-assets]:not([data-theme-loader-processed])';
  const AMBIENT_MEDIA_SELECTOR = '.media--ambient';
  let ambientMediaObserver;

  function getAmbientMedia(root = document) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    const elements = [];
    if (scope instanceof Element && scope.matches(AMBIENT_MEDIA_SELECTOR)) elements.push(scope);
    elements.push(...scope.querySelectorAll(AMBIENT_MEDIA_SELECTOR));
    return elements;
  }

  function setAmbientMediaState(element, isInView) {
    element.dataset.ambientAnimationInView = String(isInView);
    element.classList.toggle('is-ambient-animation-paused', document.hidden || !isInView);
  }

  function initAmbientMedia(root = document) {
    const elements = getAmbientMedia(root);
    if (!elements.length) return;

    if ('IntersectionObserver' in window && !ambientMediaObserver) {
      ambientMediaObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => setAmbientMediaState(entry.target, entry.isIntersecting));
      });
    }

    elements.forEach((element) => {
      if (element.dataset.ambientAnimationObserved === 'true') return;
      element.dataset.ambientAnimationObserved = 'true';

      if (ambientMediaObserver) {
        element.classList.add('is-ambient-animation-paused');
        ambientMediaObserver.observe(element);
      } else {
        setAmbientMediaState(element, true);
      }
    });
  }

  function destroyAmbientMedia(root) {
    getAmbientMedia(root).forEach((element) => {
      ambientMediaObserver?.unobserve(element);
      element.classList.remove('is-ambient-animation-paused');
      delete element.dataset.ambientAnimationObserved;
      delete element.dataset.ambientAnimationInView;
    });
  }

  function handleAmbientMediaVisibility() {
    const selector = AMBIENT_MEDIA_SELECTOR + '[data-ambient-animation-observed="true"]';
    document.querySelectorAll(selector).forEach((element) => {
      const isInView = element.dataset.ambientAnimationInView === 'true';
      element.classList.toggle('is-ambient-animation-paused', document.hidden || !isInView);
    });
  }

  function normalizeUrl(rawUrl) {
    if (!rawUrl) return '';

    try {
      return new URL(rawUrl, window.location.origin).href;
    } catch {
      return rawUrl;
    }
  }

  function isScriptAlreadyLoaded(url) {
    return Boolean(document.querySelector(`script[data-theme-loader-src="${url}"]`) || document.querySelector(`script[src="${url}"]`))
  }

  function appendScript(url) {
    const script = document.createElement('script');
    script.src = url;
    script.dataset.themeLoader = 'true';
    script.dataset.themeLoaderSrc = url;
    document.body.appendChild(script);
  }

  function themeLoadAssets(urls = []) {
    if (!Array.isArray(urls) || urls.length === 0) return;
    urls.forEach((rawUrl) => {
      const url = normalizeUrl(rawUrl);
      if (!url || window.__themeLoadedAssets.has(url)) return;
      if (isScriptAlreadyLoaded(url)) {
        window.__themeLoadedAssets.add(url);
        return;
      }
      window.__themeLoadedAssets.add(url);
      appendScript(url);
    });
  }

  function getAssetPayload(node) {
    const attrPayload = node.getAttribute('data-theme-loader-assets-json') || node.getAttribute('data-theme-loader-assets');
    const textPayload = (node.textContent || '').trim();
    const templatePayload = node instanceof HTMLTemplateElement && node.content ? (node.content.textContent || '').trim() : '';
    return (attrPayload && attrPayload.trim()) || textPayload || templatePayload || '';
  }

  function getAssetNodes(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    const nodes = [];
    if (scope instanceof Element && scope.matches(ASSET_SELECTOR)) nodes.push(scope);
    nodes.push(...scope.querySelectorAll(ASSET_SELECTOR));
    return nodes
  }

  function themeCollectAndLoadAssets(root) {
    const nodes = getAssetNodes(root);
    if (!nodes.length) return;
    const urls = [];
    nodes.forEach((node) => {
      const payload = getAssetPayload(node);
      if (!payload) {
        node.dataset.themeLoaderProcessed = 'true';
        return
      }

      try {
        const parsed = JSON.parse(payload);
        if (Array.isArray(parsed)) {
          urls.push(...parsed);
        } else if (typeof parsed === 'string' && parsed) {
          urls.push(parsed);
        }
        node.dataset.themeLoaderProcessed = 'true';
        node.removeAttribute('data-theme-loader-error');
      } catch {
        node.dataset.themeLoaderError = 'json-parse';
      }
    });

    if (urls.length) themeLoadAssets(urls);
  }

  function runWhenIdle(callback, timeout = 1500) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout });
      return
    }
    window.setTimeout(callback, 1);
  }

  function applyBrowserClass() {
    const userAgent = navigator.userAgent.toLowerCase();
    let browser = 'unknown';
    if (/edg/.test(userAgent)) browser = 'edge';
    else if (/opr|opera/.test(userAgent)) browser = 'opera';
    else if (/firefox|fxios/.test(userAgent)) browser = 'firefox';
    else if (/chrome|crios|chromium/.test(userAgent) && !/edg|opr|opera/.test(userAgent)) browser = 'chrome';
    else if (/safari/.test(userAgent) && !/chrome|crios|chromium/.test(userAgent)) browser = 'safari';
    document.documentElement.classList.add(`browser-${browser}`);
  }

  // Only body uses it (scroll lock), and it is registered as non-inherited in theme.css,
  // so writing it here does not restyle the whole page
  function updateScrollbarWidth() {
    const scrollbarWidth = `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`;
    if (document.body.style.getPropertyValue('--scrollbar-width') === scrollbarWidth) return;
    document.body.style.setProperty('--scrollbar-width', scrollbarWidth);
  }

  function updateDeviceContextClass() {
    const userAgent = navigator.userAgent || '';
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(userAgent);
    const isDesktopPlatform = /Win32|Win64|Windows|MacIntel|MacPPC|Linux x86_64|Linux armv/i.test(platform);
    const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const isNarrowViewport = window.innerWidth <= 1032;
    const isLikelyEmulatedMobile = isDesktopPlatform && isMobileUserAgent && isNarrowViewport;
    const isRealDevice = isMobileUserAgent && (hasTouch || hasCoarsePointer) && !isLikelyEmulatedMobile;
    const isResponsivePreview = isLikelyEmulatedMobile || (!isRealDevice && isNarrowViewport);
    document.documentElement.classList.toggle('is-device', isRealDevice);
    document.documentElement.classList.toggle('is-responsive', isResponsivePreview);
  }

  const SHADOW_STICKY_OFFSETS_CSS = `
    .activator-nudge {
      bottom: calc(var(--shopify-chat-activator-offset) + var(--private-preview-bar-offset, 0px) + var(--product-sticky-height, 0px) + var(--sticky-product-sticky-height, 0px)) !important;
    }

    @media screen and (max-width: 500px) {
      .activator-nudge {
        bottom: calc(var(--shopify-chat-activator-offset) + var(--private-preview-bar-offset, 0px) + var(--product-sticky-height, 0px) + var(--sticky-product-sticky-height, 0px) + var(--transcy-switcher-height, 0px)) !important;
      }
    }

    #el_ywi7qKdw8k,
    .el_ywi7qKdw8k {
      bottom: calc(12px + var(--product-sticky-height, 0px) + var(--sticky-product-sticky-height, 0px)) !important;
    }

    @media screen and (max-width: 750px) {
      #el_ywi7qKdw8k #el_sCIceYDWrJ,
      .el_ywi7qKdw8k #el_sCIceYDWrJ {
        padding: 12px 19px 12px 40px !important;
      }
      #el_sCIceYDWrJ #el_y6MqE5wcnY {
        font-size: 14px;
      }
    }

    #el_k0WH4sCvkv>svg {
      stroke: rgba(88, 48, 31, 1.00);
    }
  `;
  const SHADOW_STICKY_OFFSETS_ID = 'theme-shadow-dom-sticky-offsets';
  const observedShadowRoots = new WeakSet();
  const pendingShadowHosts = new Map();
  let shadowStickyOffsetsSheet;
  let shadowRootsObserver = null;

  function getShadowStickyOffsetsSheet() {
    if (shadowStickyOffsetsSheet !== undefined) return shadowStickyOffsetsSheet;
    try {
      shadowStickyOffsetsSheet = new CSSStyleSheet();
      shadowStickyOffsetsSheet.replaceSync(SHADOW_STICKY_OFFSETS_CSS);
    } catch {
      shadowStickyOffsetsSheet = null;
    }
    return shadowStickyOffsetsSheet;
  }

  function applyShadowStickyOffsets(shadowRoot) {
    const sheet = getShadowStickyOffsetsSheet();
    if (sheet && 'adoptedStyleSheets' in shadowRoot) {
      if (!shadowRoot.adoptedStyleSheets.includes(sheet)) shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
      return;
    }
    if (shadowRoot.getElementById(SHADOW_STICKY_OFFSETS_ID)) return;
    const style = document.createElement('style');
    style.id = SHADOW_STICKY_OFFSETS_ID;
    style.textContent = SHADOW_STICKY_OFFSETS_CSS;
    shadowRoot.appendChild(style);
  }

  function watchShadowRoot(shadowRoot) {
    applyShadowStickyOffsets(shadowRoot);
    if (observedShadowRoots.has(shadowRoot)) return;
    observedShadowRoots.add(shadowRoot);
    shadowRootsObserver?.observe(shadowRoot, { childList: true, subtree: true });
  }

  function waitForShadowHostUpgrade(tagName, root) {
    let roots = pendingShadowHosts.get(tagName);
    if (!roots) {
      roots = new Set();
      pendingShadowHosts.set(tagName, roots);
      customElements.whenDefined(tagName).then(() => {
        pendingShadowHosts.delete(tagName);
        roots.forEach((scope) => scope.querySelectorAll(tagName).forEach(scanShadowHost));
      });
    }
    roots.add(root);
  }

  function scanShadowHost(element) {
    if (element.shadowRoot) {
      watchShadowRoot(element.shadowRoot);
      scanShadowRoots(element.shadowRoot);
    } else if (element.localName.includes('-') && !customElements.get(element.localName)) {
      waitForShadowHostUpgrade(element.localName, element.getRootNode());
    }
  }

  function scanShadowRoots(root = document) {
    if (root instanceof Element) scanShadowHost(root);
    root.querySelectorAll('*').forEach(scanShadowHost);
  }

  function initShadowDomStickyOffsets() {
    const addedNodes = new Set();
    let scanFrame = null;
    const flushAddedNodes = () => {
      scanFrame = null;
      addedNodes.forEach((node) => {
        if (node.isConnected) scanShadowRoots(node);
      });
      addedNodes.clear();
    };
    shadowRootsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) addedNodes.add(node);
        });
      });
      if (addedNodes.size && scanFrame === null) scanFrame = requestAnimationFrame(flushAddedNodes);
    });
    shadowRootsObserver.observe(document.body, { childList: true });
    // Full-DOM scan is costly; app widgets mount later anyway
    runWhenIdle(() => scanShadowRoots());
    window.addEventListener('load', () => {
      [0, 5000, 30000].forEach((delay) => setTimeout(() => runWhenIdle(() => scanShadowRoots()), delay));
    }, { once: true });
  }

  // Transcy mounts its switcher after load; expose its height so the chat nudge can sit above it
  function initTranscySwitcherHeight() {
    let resizeObserver = null;
    const setHeight = (height) => document.body.style.setProperty('--transcy-switcher-height', `${Math.round(height)}px`);
    const observe = () => {
      const switcher = document.getElementById('transcyBtnSwitcher');
      if (!switcher) return false;
      resizeObserver = new ResizeObserver(() => setHeight(switcher.offsetHeight));
      resizeObserver.observe(switcher);
      return true;
    };
    if (observe()) return;
    const mountObserver = new MutationObserver(() => {
      if (!observe()) return;
      mountObserver.disconnect();
    });
    mountObserver.observe(document.body, { childList: true, subtree: true });
  }

  function flushThemeDeferQueue() {
    if (!Array.isArray(window.ThemeDefer) || window.ThemeDefer.length === 0) return;
    while (window.ThemeDefer.length) {
      const callback = window.ThemeDefer.shift();
      if (typeof callback === 'function') callback();
    }
  }

  function initDetailsAccessibility(root = document) {
    root.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      summary.setAttribute('role', 'button');
      const details = summary.parentNode;
      const content = summary.nextElementSibling;
      summary.setAttribute('aria-expanded', details.hasAttribute('open'));
      if (content && content.id) summary.setAttribute('aria-controls', content.id);
      summary.addEventListener('click', (event) => {
        const el = event.currentTarget.closest('details');
        event.currentTarget.setAttribute('aria-expanded', !el.hasAttribute('open'));
      });
      if (summary.closest('menu-drawer')) return;
      details.addEventListener('keyup', onKeyUpEscape);
    });
  }

  function handleDomContentLoaded() {
    document.body.classList.add('page-load');
    document.dispatchEvent(new CustomEvent('page:loaded'));
    updateDeviceContextClass();
    initDetailsAccessibility();
    initAmbientMedia();
    runWhenIdle(() => {
      // Only needed once a drawer/modal opens: measure when layout is already clean instead of forcing it now
      updateScrollbarWidth();
      applyBrowserClass();
      initTranscySwitcherHeight();
      flushThemeDeferQueue();
      themeCollectAndLoadAssets(document);
    });
  }

  const handleResize = (() => {
    let ticking = false;

    return () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        updateScrollbarWidth();
        updateDeviceContextClass();
      });
    };
  })();

  initShadowDomStickyOffsets();
  window.themeLoadAssets ||= themeLoadAssets;
  window.themeCollectAndLoadAssets ||= themeCollectAndLoadAssets;
  window.addEventListener('beforeunload', () => {
    document.body.classList.add('page-unload');
  });
  window.addEventListener('DOMContentLoaded', handleDomContentLoaded);
  window.addEventListener('resize', handleResize);
  document.addEventListener('visibilitychange', handleAmbientMediaVisibility);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document.body.classList.remove('page-unload');
    }
  });
  document.addEventListener('shopify:section:load', (event) => {
    const target = event?.target || document;
    themeCollectAndLoadAssets(target);
    initDetailsAccessibility(target);
    initAmbientMedia(target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroyAmbientMedia(event?.target || document);
  });
})();

// Expand blog summaries only into space left by the original two-line layout.
(() => {
  const updateBlogSummaryLines = () => {
    const cards = [...document.querySelectorAll('.section__main-blog .card-article')]
      .map((card) => ({
        summary: card.querySelector('.card__summary'),
        button: card.querySelector('.card__information > .card__buttons'),
      }))
      .filter(({ summary, button }) => summary && button);

    // Measure the original layout each time, never the previously expanded one.
    cards.forEach(({ summary }) => {
      summary.style.removeProperty('--ellipsis');
    });

    const measurements = cards.map(({ summary, button }) => {
      if (!summary.getClientRects().length || !button.getClientRects().length) return null;
      const style = getComputedStyle(summary);
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
      const baseline = parseInt(style.getPropertyValue('--ellipsis'), 10) || 2;
      const summaryRect = summary.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const marginBottom = parseFloat(style.marginBottom) || 0;
      // Keep the button's original CSS padding intact.
      const space = buttonRect.top - summaryRect.top - marginBottom;
      const lines = Math.max(baseline, Math.floor(space / lineHeight));
      return { summary, lines };
    });

    measurements.forEach((measurement) => {
      if (!measurement) return;
      measurement.summary.style.setProperty('--ellipsis', measurement.lines);
    });
  };

  let frame;
  const scheduleUpdate = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(updateBlogSummaryLines);
  };
  scheduleUpdate();
  window.addEventListener('load', scheduleUpdate, { once: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  document.addEventListener('shopify:section:load', scheduleUpdate);
  if (document.fonts) document.fonts.ready.then(scheduleUpdate);
})();

// Temporary keyboard/focus inspector. Enable with ?a11y-debug=1.
// (() => {
//   if (new URLSearchParams(window.location.search).get("a11y-debug") !== "1") return;

//   const panel = document.createElement("output");
//   panel.id = "A11yFocusDebug";
//   panel.setAttribute("aria-live", "off");
//   Object.assign(panel.style, {
//     position: "fixed",
//     right: "1rem",
//     bottom: "1rem",
//     zIndex: "2147483647",
//     maxWidth: "min(42rem, calc(100vw - 2rem))",
//     padding: "1rem",
//     border: "2px solid #fff",
//     borderRadius: ".4rem",
//     background: "#111",
//     color: "#fff",
//     font: "14px/1.4 monospace",
//     whiteSpace: "pre-wrap",
//     pointerEvents: "none",
//     boxShadow: "0 0 0 2px #111"
//   });
//   document.body.append(panel);

//   const getName = (element) => {
//     if (!(element instanceof Element)) return "";
//     const labelledBy = (element.getAttribute("aria-labelledby") || "")
//       .split(/\s+/)
//       .filter(Boolean)
//       .map((id) => document.getElementById(id)?.textContent?.trim())
//       .filter(Boolean)
//       .join(" ");
//     return element.getAttribute("aria-label")
//       || labelledBy
//       || element.getAttribute("alt")
//       || element.getAttribute("title")
//       || element.textContent?.replace(/\s+/g, " ").trim().slice(0, 120)
//       || "";
//   };

//   const describe = (element) => {
//     if (!(element instanceof Element)) return String(element);
//     const classes = [...element.classList].slice(0, 4).map((name) => `.${name}`).join("");
//     const selector = `${element.tagName.toLowerCase()}${element.id ? `#${element.id}` : ""}${classes}`;
//     const role = element.getAttribute("role") || element.tagName.toLowerCase();
//     const name = getName(element);
//     return `${selector} | role: ${role}${name ? ` | name: ${name}` : ""}`;
//   };
//   const loggedEmptyAltImages = new WeakSet();
//   const logEmptyImageAlts = (root = document, force = false) => {
//     const images = [
//       ...(root instanceof HTMLImageElement ? [root] : []),
//       ...(typeof root.querySelectorAll === "function" ? root.querySelectorAll("img") : [])
//     ].filter((image) => {
//       const hasEmptyAlt = !image.hasAttribute("alt") || image.getAttribute("alt").trim() === "";
//       return hasEmptyAlt && (force || !loggedEmptyAltImages.has(image));
//     });
//     if (!images.length) return;

//     console.groupCollapsed(`[A11y alt debug] ${images.length} image(s) with empty or missing alt`);
//     images.forEach((image, index) => {
//       loggedEmptyAltImages.add(image);
//       console.warn(`${index + 1}. ${image.hasAttribute("alt") ? "Empty alt" : "Missing alt"}`, {
//         image,
//         src: image.currentSrc || image.src,
//         selector: describe(image),
//         html: image.outerHTML.slice(0, 500)
//       });
//     });
//     console.groupEnd();
//   };

//   let altScanFrame;
//   const scheduleAltScan = () => {
//     window.cancelAnimationFrame(altScanFrame);
//     altScanFrame = window.requestAnimationFrame(() => logEmptyImageAlts());
//   };
//   const altObserver = new MutationObserver(scheduleAltScan);


//   let lastKey = "Page load";
//   const render = (eventName, target) => {
//     window.requestAnimationFrame(() => {
//       const activeElement = document.activeElement;
//       panel.textContent = [
//         `Event: ${eventName}`,
//         `Key: ${lastKey}`,
//         `Target: ${describe(target)}`,
//         `Active: ${describe(activeElement)}`
//       ].join("\n");
//       console.log(`[A11y focus debug] ${eventName}`, {
//         key: lastKey,
//         target,
//         activeElement,
//         targetDescription: describe(target),
//         activeDescription: describe(activeElement)
//       });
//     });
//   };

//   const onKeyDown = (event) => {
//     if (event.key !== "Tab" && event.key !== "Enter") return;
//     lastKey = `${event.shiftKey ? "Shift+" : ""}${event.key}`;
//     render("keydown", event.target);
//     window.setTimeout(() => render("after keydown", event.target));
//   };
//   const onFocusIn = (event) => render("focusin", event.target);

//   document.addEventListener("keydown", onKeyDown, true);
//   document.addEventListener("focusin", onFocusIn, true);
//   altObserver.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["alt", "src"] });
//   window.logEmptyImageAlts = () => logEmptyImageAlts(document, true);
//   logEmptyImageAlts();
//   render("enabled", document.activeElement);

//   window.stopA11yFocusDebug = () => {
//     document.removeEventListener("keydown", onKeyDown, true);
//     document.removeEventListener("focusin", onFocusIn, true);
//     altObserver.disconnect();
//     window.cancelAnimationFrame(altScanFrame);
//     delete window.logEmptyImageAlts;
//     panel.remove();
//     delete window.stopA11yFocusDebug;
//   };
// })();

// Third-party embed code (Instagram feed...) kept in a <template> until it nears the viewport
class LazyEmbed extends HTMLElement {
  connectedCallback() {
    createObserver({
      element: this,
      callback: this.render.bind(this),
      threshold: 300
    });
  }

  render() {
    const template = this.querySelector(':scope > template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    // Scripts coming from a template never run: recreate them, keeping their order
    content.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => script.setAttribute(attribute.name, attribute.value));
      script.textContent = oldScript.textContent;
      if (script.src) script.async = false;
      oldScript.replaceWith(script);
    });
    template.replaceWith(content);
    this.dispatchEvent(new CustomEvent('lazy-embed:loaded', { bubbles: true }));
  }
}
customElements.define('lazy-embed', LazyEmbed);

// Lazy sections: markup from snippets/lazy-section.liquid is kept in <template id="lazy-section-{id}">
// and swapped into its #inject-{id} placeholder once that placeholder nears the viewport.
// window.themeInitLazySections(root) can be called again for markup added later.
(() => {
  const renderLazySection = (template, placeholder) => {
    const section = placeholder.parentElement;
    placeholder.replaceWith(template.content);
    template.remove();
    if (section) {
      window.themeCollectAndLoadAssets?.(section);
      section.dispatchEvent(new CustomEvent('lazy-section:loaded', { bubbles: true }));
    }
  };

  let observer;
  const getObserver = () => {
    observer ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const template = document.getElementById(`lazy-section-${entry.target.dataset.lazySectionId}`);
          if (template) renderLazySection(template, entry.target);
        });
      },
      { rootMargin: '300px 0px' }
    );
    return observer;
  };

  const initLazySections = (root = document) => {
    root.querySelectorAll('template[id^="lazy-section-"]').forEach((template) => {
      const id = template.id.replace('lazy-section-', '');
      const placeholder = document.getElementById(`inject-${id}`);
      if (!placeholder || placeholder.dataset.lazySectionId) return;
      placeholder.dataset.lazySectionId = id;
      getObserver().observe(placeholder);
    });
  };

  window.themeInitLazySections ||= initLazySections;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initLazySections(), { once: true });
  } else {
    initLazySections();
  }
})();
